import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { 
    LiveKitRoom, 
    VideoConference, 
    RoomAudioRenderer,
    useRoomContext,
    useParticipants,
    useLocalParticipant
} from '@livekit/components-react';
import '@livekit/components-styles/index.css';
import { Track } from 'livekit-client';
import axios from 'axios';
import { SECRETS_ENV } from '../../../../../../Config/firebase';
import { db } from '../../../../../../Config/firebase';
import { doc, updateDoc, serverTimestamp, setDoc, getDoc, collection, addDoc } from 'firebase/firestore';
import ChequeredChess from '../ChequeredChess';
import PollsQuizzes from '../PollsQuizzes';

// Import modular components
import { CustomLayoutManager } from './CustomLayoutManager';
import { BackgroundEffectsControl } from './BackgroundEffectsControl';
import { LiveKitRoomProps } from './types';

// Import styles
import './LiveKitLayout.css';

const API_ENDPOINT = SECRETS_ENV.API_END_POINT;

// Internal helper to expose LiveKit room instance via callback
const RoomRefCollector: React.FC<{ onRoom: (room: any) => void }> = ({ onRoom }) => {
    const room = useRoomContext();
    useEffect(() => {
        if (room) onRoom(room);
    }, [room, onRoom]);
    return null;
};

// Chess component wrapper that uses room context from LiveKit
const ChessInMeeting: React.FC<{
    subscriptionId: string;
    subscriptionName: string;
    userRole?: string;
    participantName: string;
}> = (props) => {
    const room = useRoomContext();
    const participants = useParticipants();
    const { localParticipant } = useLocalParticipant();
    
    // Validate required props
    if (!props.subscriptionId || !props.subscriptionName || !props.participantName) {
        console.error('⚠️ Missing required props for ChessInMeeting:', {
            subscriptionId: props.subscriptionId,
            subscriptionName: props.subscriptionName,
            participantName: props.participantName,
            userRole: props.userRole
        });
        return null;
    }

    // Validate room context
    if (!room) {
        console.warn('⚠️ Room context not available for chess');
        return null;
    }

    // Validate room state
    if (!localParticipant) {
        console.warn('⚠️ Local participant not ready');
        return null;
    }

    console.log('♟️ Initializing ChessInMeeting:', {
        userRole: props.userRole,
        participantName: props.participantName,
        roomName: room.name,
        roomState: room.state,
        hasLocalParticipant: !!localParticipant,
        participantCount: participants.length,
        localParticipantId: localParticipant.identity,
        localParticipantMetadata: localParticipant.metadata,
        participants: participants.map(p => p.identity)
    });
    
    try {
        return (
            <ChequeredChess
                {...props}
                isInMeeting={true}
                room={room}
                disableTeachingMode={true}
                isGuest={props.userRole === 'student'} // Treat all students as potential guests
            />
        );
    } catch (error) {
        console.error('❌ Error rendering ChessInMeeting:', error);
        return (
            <div className="p-4 bg-red-100 text-red-700 rounded-lg">
                <p className="font-semibold">Error loading chess component</p>
                <p className="text-sm opacity-75">Please try refreshing the page</p>
            </div>
        );
    }
};

// Polls component wrapper
const PollsInMeeting: React.FC<{
    subscriptionId: string;
    subscriptionName: string;
    userRole?: string;
    participantName: string;
    tenantId: string;
}> = (props) => {
    const room = useRoomContext();
    const participants = useParticipants();
    const { localParticipant } = useLocalParticipant();
    
    if (!room) {
        console.warn('⚠️ Room context not available for polls');
        return null;
    }

    console.log('📊 Initializing PollsQuizzes with room:', {
        roomName: room.name,
        roomState: room.state,
        hasLocalParticipant: !!localParticipant,
        participantCount: participants.length
    });
    
    return (
        <PollsQuizzes
            {...props}
            isInMeeting={true}
            room={room}
            serviceProviderId={props.tenantId} // Required for Firestore operations
        />
    );
};

// Activity Notification Listener Component
const ActivityNotificationListener: React.FC<{
    userRole: 'teacher' | 'student';
    onPuzzleShared: (data: any) => void;
    onPollStarted: (data: any) => void;
    onChatMessage: (data: any) => void;
    onShowChessBoard?: (show: boolean) => void;
    onShowPollsQuizzes?: (show: boolean) => void;
}> = ({ userRole, onPuzzleShared, onPollStarted, onChatMessage, onShowChessBoard, onShowPollsQuizzes }) => {
    const room = useRoomContext();
    const participants = useParticipants();
    
    // Log initial component state
    useEffect(() => {
        console.log('🏁 ActivityNotificationListener initial state:', {
            userRole,
            hasRoom: !!room,
            roomName: room?.name || 'not connected',
            roomState: room?.state || 'not connected',
            participantCount: participants.length
        });
    }, [room, userRole, participants.length]);

    useEffect(() => {
        if (!room) return;

        const handleData = (data: any) => {
            try {
                console.log('📨 Raw data received:', {
                    dataType: typeof data,
                    hasPayload: !!data.payload,
                    payloadType: typeof data.payload,
                    isArray: Array.isArray(data.payload),
                    isUint8: data.payload instanceof Uint8Array,
                    fullData: data
                });
                
                let message;
                
                // Handle different data formats
                console.log('🔍 Processing data:', {
                    dataKeys: Object.keys(data),
                    dataValues: Object.values(data),
                    dataEntries: Object.entries(data)
                });
                
                // Try to extract the actual payload from LiveKit's data event
                // The data might be in data.data, data.message, or just data itself
                const rawPayload = data.data || data.message || data.payload || data;
                
                console.log('🔍 Extracted raw payload:', {
                    rawPayload,
                    rawPayloadType: typeof rawPayload,
                    isUint8: rawPayload instanceof Uint8Array,
                    isArray: Array.isArray(rawPayload),
                    length: rawPayload?.length
                });
                
                if (rawPayload instanceof Uint8Array || Array.isArray(rawPayload)) {
                    // Raw Uint8Array or Array
                    if (rawPayload.length === 0) {
                        console.warn('⚠️ Empty data received, skipping');
                        return;
                    }
                    const decoder = new TextDecoder();
                    const payload = rawPayload instanceof Uint8Array ? rawPayload : new Uint8Array(rawPayload);
                    const jsonString = decoder.decode(payload);
                    console.log('🔍 Decoded JSON string:', jsonString);
                    message = JSON.parse(jsonString);
                } else if (typeof rawPayload === 'string') {
                    // Already a string
                    if (rawPayload.length === 0) {
                        console.warn('⚠️ Empty string payload, skipping');
                        return;
                    }
                    console.log('🔍 Parsing JSON string:', rawPayload);
                    message = JSON.parse(rawPayload);
                } else {
                    // Already parsed or unknown format
                    console.log('🔍 Using raw payload as message:', rawPayload);
                    message = rawPayload;
                }
                
                console.log('📨 Received data:', {
                    type: message.type,
                    action: message.action,
                    sender: message.name || message.playerName,
                    timestamp: new Date().toISOString(),
                    fullMessage: message
                });
                
                // Handle poll messages (type: 'poll' with action)
                if (message.type === 'poll' && message.action === 'POLL_STARTED') {
                    console.log('📊 Poll started:', message);
                    // Both teachers and students receive poll messages
                    if (onShowPollsQuizzes) {
                        onShowPollsQuizzes(true);
                    }
                    if (onPollStarted) {
                        onPollStarted({
                            ...message,
                            isTeacherView: userRole === 'teacher'
                        });
                    }
                    if (userRole === 'student') {
                        toast.info('Teacher started a poll!');
                    }
                }
                
                switch (message.type) {
                    case 'chess':
                        // Handle chess/puzzle messages
                        if (message.action === 'PUZZLE_SHARED') {
                            console.log('♟️ Puzzle shared:', message);
                            // Both students/guests and teacher receive puzzle
                            if (onShowChessBoard) {
                                onShowChessBoard(true);
                            }
                            if (onPuzzleShared) {
                                onPuzzleShared({
                                    ...message,
                                    isTeacherView: userRole === 'teacher',
                                    isGuest: userRole === 'student' && message.metadata?.isGuest
                                });
                            }
                            if (userRole === 'student') {
                                toast.info('Teacher shared a puzzle!');
                                console.log('🎯 Opening chess board for student/guest');
                            }
                        } else if (message.action === 'BOARD_VIEW_SHARED') {
                            console.log('📺 Board view shared:', message);
                            if (onShowChessBoard) {
                                onShowChessBoard(true);
                            }
                            if (userRole === 'student') {
                                toast.info('Teacher is demonstrating the board');
                            }
                        } else if (message.action === 'PUZZLE_STOPPED' || message.action === 'BOARD_VIEW_STOPPED') {
                            console.log('♟️ Puzzle stopped');
                            if (onShowChessBoard) {
                                onShowChessBoard(false);
                            }
                            if (userRole === 'student') {
                                toast.info('Teacher stopped sharing');
                            }
                        } else if (message.action === 'STUDENT_MOVE') {
                            console.log('♟️ Student move:', message);
                            // Teacher sees moves
                            if (userRole === 'teacher') {
                                if (onPuzzleShared) {
                                    onPuzzleShared({
                                        ...message,
                                        isTeacherView: true
                                    });
                                }
                                    toast.info(`${message.playerName} made a move: ${message.san}`);
                            }
                        } else if (message.action === 'PUZZLE_COMPLETED') {
                            console.log('♟️ Puzzle completed:', message);
                            // Teacher sees completion
                            if (userRole === 'teacher') {
                                if (onPuzzleShared) {
                                    onPuzzleShared({
                                        ...message,
                                        isTeacherView: true
                                    });
                                }
                                    toast.success(`${message.playerName} completed the puzzle!`);
                            }
                        }
                        break;
                    case 'puzzle_shared':
                        // Legacy support
                        console.log('♟️ Legacy puzzle shared:', message);
                        onPuzzleShared({
                            ...message,
                            isTeacherView: userRole === 'teacher'
                        });
                        break;
                    case 'chat_message':
                        console.log('💬 Chat message received by ActivityNotificationListener:', {
                            messageType: message.type,
                            from: message.name,
                            messageText: message.message,
                            isTeacher: message.isTeacher,
                            userRole,
                            hasOnChatMessage: !!onChatMessage
                        });
                        // Everyone receives chat messages
                        if (onChatMessage) {
                            console.log('📞 Calling onChatMessage callback');
                            onChatMessage(message);
                        } else {
                            console.error('❌ onChatMessage callback is not defined!');
                        }
                        break;
                    default:
                        console.log('❓ Unknown message type:', message.type);
                }
            } catch (error) {
                console.error('Error parsing data message:', error);
            }
        };

        room.on('dataReceived', handleData);
        
        return () => {
            room.off('dataReceived', handleData);
        };
    }, [room, userRole, onPuzzleShared, onPollStarted, onChatMessage]);

    return null;
};


const LiveKitRoomComponent: React.FC<LiveKitRoomProps> = ({
    roomName,
    meetingTitle,
    token,
    serverUrl,
    participantName,
    userRole = 'student',
    tenantId,
    subscriptionId = '',
    subscriptionName = '',
    onDisconnect,
    onError
}) => {
    const lkRoomRef = useRef<any>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isRecording, setIsRecording] = useState(false);
    const [isRecordingLoading, setIsRecordingLoading] = useState(false);
    const recordingIdRef = useRef<string | null>(null);
    const [recordingMode, setRecordingMode] = useState<'cloud' | 'local' | null>(null);
    // Default to 'cloud' always - only use 'local' if user explicitly set it
    const [recordingPreference, setRecordingPreference] = useState<'local' | 'cloud'>(() => {
        const saved = localStorage.getItem('meetplus_recording_preference');
        // Only use 'local' if user explicitly set it, otherwise always default to 'cloud'
        return saved === 'local' ? 'local' : 'cloud';
    });
    const [meetingStartTime, setMeetingStartTime] = useState<number | null>(null);
    const [subscriptionHoursRemaining, setSubscriptionHoursRemaining] = useState<number | null>(null);
    const [initialHoursAtMeetingStart, setInitialHoursAtMeetingStart] = useState<number | null>(null);
    const [showPurchaseHoursDialog, setShowPurchaseHoursDialog] = useState(false);
    const [showHoursInfoDialog, setShowHoursInfoDialog] = useState(false);
    const [selectedCloudRes, setSelectedCloudRes] = useState<'720p' | '480p'>(
        (localStorage.getItem('meetplus_cloud_resolution') as '720p' | '480p') || '720p'
    );
    const [selectedLocalRes, setSelectedLocalRes] = useState<'1080p' | '720p'>('720p');
    const [recordLayout, setRecordLayout] = useState<'auto' | 'pip-circle' | 'side-by-side'>(
        (localStorage.getItem('meetplus_cloud_layout') as 'auto' | 'pip-circle' | 'side-by-side') || 'auto'
    );
    const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
    const maxTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [showBackgroundEffects, setShowBackgroundEffects] = useState(false);
    const [recordingStartMs, setRecordingStartMs] = useState<number | null>(null);
    const [currentEffect, setCurrentEffect] = useState<'none' | 'blur' | 'image'>('none');
    const [showChessBoard, setShowChessBoard] = useState(false);
    const [showPollsQuizzes, setShowPollsQuizzes] = useState(false);
    const [teacherParticipantId, setTeacherParticipantId] = useState<string | null>(null);
    const [pinnedParticipant, setPinnedParticipant] = useState<string | null>(null);
    const [currentLayout, setCurrentLayout] = useState<'grid' | 'focus' | 'screen-share'>('focus');
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const [, setScreenSharingParticipantId] = useState<string | null>(null);
    const [isMirrored, setIsMirrored] = useState<boolean>(false); // Disabled by default to prevent auto-flip
    const [showChat, setShowChat] = useState(false);
    const [minimizeVideos, setMinimizeVideos] = useState(false);
    const [chatExpanded, setChatExpanded] = useState(false); // New state for chat expansion
    const [isMinimized, setIsMinimized] = useState(false); // Picture-in-picture mode when screen sharing
    const [minimizedPosition, setMinimizedPosition] = useState({ x: window.innerWidth - 380, y: 100 }); // Default position for minimized window
    const [chatMessages, setChatMessages] = useState<Array<{name: string, message: string, time: string, id: string}>>([]);
    const [chatInput, setChatInput] = useState('');
    const [showControls, setShowControls] = useState(true); // Always visible to prevent dark overlay
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);
    const [showMeetingInfo, setShowMeetingInfo] = useState(false);
    const hideControlsTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [micEnabled, setMicEnabled] = useState(true);
    const [cameraEnabled, setCameraEnabled] = useState(true);
    const [, setForceUpdate] = useState(0);
    const [selectedAvatar] = useState<string>('');
    const [, setRecordingTimer] = useState(0); // Force re-render for recording timer
    const [pollsPanelWidth, setPollsPanelWidth] = useState(400); // Polls panel width state
    const [showParticipantsPanel, setShowParticipantsPanel] = useState(false);
    
    // Local recording refs
    const inAppRecordVideoElsRef = useRef<HTMLVideoElement[]>([]);
    const inAppCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const inAppRecordRafRef = useRef<number | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const audioDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
    const inAppVideoStreamRef = useRef<MediaStream | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const recordedChunksRef = useRef<Blob[]>([]);
    const localRecordingExtRef = useRef<'mp4' | 'webm'>('webm');
    const attachedTrackElsRef = useRef<Array<{ track: any; el: HTMLVideoElement }>>([]);
    const meetingRootRef = useRef<HTMLDivElement | null>(null);
    const isIntentionalDisconnectRef = useRef<boolean>(false); // Track intentional exits
    
    // Device selection states
    const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
    const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
    const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');
    const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');
    const [showAudioDevices, setShowAudioDevices] = useState(false);
    const [showVideoDevices, setShowVideoDevices] = useState(false);

    // Persist recording preference to localStorage
    useEffect(() => {
        localStorage.setItem('meetplus_recording_preference', recordingPreference);
        console.log('💾 Saved recording preference to localStorage:', recordingPreference);
    }, [recordingPreference]);

    // Persist cloud resolution to localStorage
    useEffect(() => {
        localStorage.setItem('meetplus_cloud_resolution', selectedCloudRes);
        console.log('💾 Saved cloud resolution to localStorage:', selectedCloudRes);
    }, [selectedCloudRes]);

    // Persist cloud layout to localStorage
    useEffect(() => {
        localStorage.setItem('meetplus_cloud_layout', recordLayout);
        console.log('💾 Saved cloud layout to localStorage:', recordLayout);
    }, [recordLayout]);

    // Recording timer - updates every second when recording  
    useEffect(() => {
        if (!isRecording) return;
        
        const interval = setInterval(() => {
            setRecordingTimer(prev => prev + 1);
        }, 1000);
        
        return () => clearInterval(interval);
    }, [isRecording, setRecordingTimer]);

    // Fetch meetPlusHours from serviceProviders document
    const fetchSubscriptionHours = useCallback(async (): Promise<{ canStart: boolean; hours: number | null }> => {
        if (!tenantId || userRole !== 'teacher') return { canStart: true, hours: null };
        
        try {
            const serviceProviderRef = doc(db, 'ServiceProviders', tenantId);
            const serviceProviderSnap = await getDoc(serviceProviderRef);
            
            if (serviceProviderSnap.exists()) {
                const data = serviceProviderSnap.data();
                let meetPlusHours = data.meetPlusHours || 0;
                
                // If no hours configured, assume 5 trial hours (display only, will be granted on first meeting end)
                if (meetPlusHours === 0 || meetPlusHours === null || meetPlusHours === undefined) {
                    meetPlusHours = 5; // Display 5 hours, but haven't granted yet
                }
                
                setSubscriptionHoursRemaining(meetPlusHours);
                
                console.log('📊 Meet+ hours remaining:', { meetPlusHours });
                
                // Check if meeting can start
                // Allow if no hours configured (will get 5 trial on first meeting end)
                // Or if they have at least 1 hour remaining
                const hasNoConfig = (data.meetPlusHours === 0 || data.meetPlusHours === null || data.meetPlusHours === undefined);
                if (!hasNoConfig && meetPlusHours < 1) {
                    // Show custom dialog instead of browser alert
                    setShowPurchaseHoursDialog(true);
                    onDisconnect(false);
                    return { canStart: false, hours: meetPlusHours };
                }
                
                return { canStart: true, hours: meetPlusHours };
            }
        } catch (error) {
            console.error('Error fetching service provider hours:', error);
        }
        return { canStart: true, hours: null };
    }, [tenantId, userRole, onDisconnect]);

    // Periodically refresh subscription hours balance during active meetings (teachers only)
    // Also update hours in real-time by subtracting elapsed meeting time
    useEffect(() => {
        if (!isConnected || userRole !== 'teacher' || !tenantId) return;

        // Refresh immediately, then every minute
        fetchSubscriptionHours();
        
        const interval = setInterval(async () => {
            // If meeting is active, calculate real-time remaining hours using initial balance
            if (meetingStartTime && initialHoursAtMeetingStart !== null) {
                const elapsedHours = (Date.now() - meetingStartTime) / (1000 * 60 * 60); // hours
                const realTimeRemaining = initialHoursAtMeetingStart - elapsedHours;
                
                // Update display with real-time remaining
                setSubscriptionHoursRemaining(Math.max(0, realTimeRemaining));
                
                // Note: We DON'T update Firestore during the meeting to avoid double-deduction
                // Firestore is only updated when the meeting ends (in stopMeetingHoursTracking)
                // This ensures accurate deduction and prevents race conditions
            } else {
                // If no active meeting, just refresh from Firestore
            fetchSubscriptionHours();
            }
        }, 60000); // Every 60 seconds

        return () => clearInterval(interval);
    }, [isConnected, userRole, tenantId, fetchSubscriptionHours, meetingStartTime, initialHoursAtMeetingStart]);

    // DISABLED: Auto-restore background effect to prevent first-time blur
    // User will apply effects manually
    // useEffect(() => {
    //     const savedEffect = localStorage.getItem('meetplus_background_effect');
    //     if (savedEffect && ['none', 'blur', 'image'].includes(savedEffect)) {
    //         setCurrentEffect(savedEffect as 'none' | 'blur' | 'image');
    //         console.log('🔄 Restored saved background effect:', savedEffect);
    //     }
    // }, []);
    useEffect(() => {
        const handleLiveKitError = (event: any) => {
            console.warn('LiveKit internal error (non-critical):', event);
        };

        // Add global error handler for LiveKit
        window.addEventListener('error', (event) => {
            if (event.message?.includes('Element not part of the array') || 
                event.message?.includes('camera_placeholder')) {
                console.warn('LiveKit placeholder error (non-critical):', event.message);
                event.preventDefault(); // Prevent error from showing in console
            }
        });

        return () => {
            window.removeEventListener('error', handleLiveKitError);
        };
    }, []);
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Only handle spacebar when not typing in input fields
            if (event.code === 'Space' && 
                !['INPUT', 'TEXTAREA'].includes((event.target as HTMLElement)?.tagName)) {
                event.preventDefault();
                
                // Toggle microphone
                if (lkRoomRef.current?.localParticipant) {
                    const participant = lkRoomRef.current.localParticipant;
                    const currentState = participant.isMicrophoneEnabled;
                    const newState = !currentState;
                    
                    participant.setMicrophoneEnabled(newState).then(() => {
                        setMicEnabled(newState);
                        setForceUpdate(prev => prev + 1);
                        toast.success(`Microphone ${newState ? 'unmuted' : 'muted'} (Spacebar)`);
                    }).catch((error) => {
                        console.error('Error toggling microphone:', error);
                        toast.error('Failed to toggle microphone');
                    });
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);
    const enumerateDevices = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = devices.filter(device => device.kind === 'audioinput');
            const videoInputs = devices.filter(device => device.kind === 'videoinput');
            
            setAudioDevices(audioInputs);
            setVideoDevices(videoInputs);
            
            // Set default devices if not already selected
            if (!selectedAudioDevice && audioInputs.length > 0) {
                setSelectedAudioDevice(audioInputs[0].deviceId);
            }
            if (!selectedVideoDevice && videoInputs.length > 0) {
                setSelectedVideoDevice(videoInputs[0].deviceId);
            }
            
            console.log('🎤 Audio devices:', audioInputs.map(d => ({ id: d.deviceId, label: d.label })));
            console.log('📹 Video devices:', videoInputs.map(d => ({ id: d.deviceId, label: d.label })));
        } catch (error) {
            console.error('Error enumerating devices:', error);
        }
    };

    // Switch audio device
    const switchAudioDevice = async (deviceId: string) => {
        if (!lkRoomRef.current?.localParticipant) {
            console.log('❌ No room or participant available');
            return;
        }
        
        try {
            console.log('🎤 Switching to audio device:', deviceId);
            await lkRoomRef.current.localParticipant.setMicrophoneEnabled(false);
            await lkRoomRef.current.localParticipant.setMicrophoneEnabled(true, { deviceId });
            setSelectedAudioDevice(deviceId);
            setShowAudioDevices(false);
            toast.success('Microphone device switched');
        } catch (error) {
            console.error('Error switching audio device:', error);
            toast.error('Failed to switch microphone device');
        }
    };

    // Switch video device
    const switchVideoDevice = async (deviceId: string) => {
        if (!lkRoomRef.current?.localParticipant) {
            console.log('❌ No room or participant available');
            return;
        }
        
        try {
            console.log('📹 Switching to video device:', deviceId);
            await lkRoomRef.current.localParticipant.setCameraEnabled(false);
            await lkRoomRef.current.localParticipant.setCameraEnabled(true, { deviceId });
            setSelectedVideoDevice(deviceId);
            setShowVideoDevices(false);
            toast.success('Camera device switched');
        } catch (error) {
            console.error('Error switching video device:', error);
            toast.error('Failed to switch camera device');
        }
    };

    // Layout management - Zoom-like behavior
    useEffect(() => {
        if (userRole === 'teacher') {
            setCurrentLayout('grid'); // Teacher sees grid view by default
        } else {
            setCurrentLayout('focus'); // Students see focus view (teacher as presenter)
        }
    }, [userRole]);

    // Handle screen share detection - completely rewritten
    const handleScreenShareDetected = useCallback((isSharing: boolean, participantId?: string) => {
        console.log('🖥️ Screen share detected:', { isSharing, participantId, userRole });
        
        if (isSharing && participantId) {
            // Someone started screen sharing
            setScreenSharingParticipantId(participantId);
            setCurrentLayout('screen-share');
            
            // Only set isScreenSharing to true if THIS participant is sharing
            if (participantId === lkRoomRef.current?.localParticipant?.identity) {
                setIsScreenSharing(true);
                console.log('🖥️ Local participant is screen sharing');
            } else {
                setIsScreenSharing(false);
                console.log('🖥️ Remote participant is screen sharing:', participantId);
            }
        } else {
            // Screen sharing stopped
            setScreenSharingParticipantId(null);
            setIsScreenSharing(false);
            
            // Return to default layout based on role
            if (userRole === 'teacher') {
                setCurrentLayout('grid');
            } else {
                setCurrentLayout('focus');
            }
            console.log('🖥️ Screen sharing stopped, returning to default layout');
        }
    }, [userRole]);

    // Handle teacher identification
    const handleTeacherIdentified = useCallback((teacherId: string) => {
        setTeacherParticipantId(teacherId);
        console.log('👨‍🏫 Teacher identified:', teacherId);
    }, []);

    // Handle participant pinning
    const handleParticipantPin = useCallback((participantId: string) => {
        setPinnedParticipant(prev => prev === participantId ? null : participantId);
    }, []);

    // Handle puzzle sharing - completely rewritten
    const handlePuzzleShared = useCallback((data: any) => {
        console.log('♟️ Puzzle event received:', data);

        // Handle different puzzle events
        if (data.action === 'PUZZLE_SHARED') {
            if (userRole === 'student') {
                toast.info('Teacher shared a puzzle!');
            }
        } else if (data.action === 'STUDENT_MOVE' && userRole === 'teacher') {
            // Teacher receives student moves in real-time
            console.log('♟️ Student move:', {
                student: data.playerName,
                move: data.san,
                isCorrect: data.isCorrect
            });
            toast.info(`${data.playerName} made a move: ${data.san}`);
        } else if (data.action === 'PUZZLE_COMPLETED' && userRole === 'teacher') {
            // Teacher receives puzzle completion
            console.log('♟️ Puzzle completed by:', data.playerName);
            toast.success(`${data.playerName} completed the puzzle!`);
        }
    }, [userRole]);

    // Handle poll started
    const handlePollStarted = useCallback(() => {
        setShowPollsQuizzes(true);
        toast.info('Teacher started a poll!');
    }, []);

    // Handle chat messages
    const handleChatMessage = useCallback((data: any) => {
        if (!data.name || !data.message) {
            console.warn('⚠️ Invalid chat message:', data);
            return;
        }

        console.log('📨 Received chat message:', {
            from: data.name,
            myName: participantName,
            isTeacher: data.isTeacher,
            message: data.message,
            timestamp: new Date(data.timestamp || Date.now()).toLocaleTimeString()
        });

        const newMessage = {
            name: data.name,
            message: data.message,
            time: new Date(data.timestamp || Date.now()).toLocaleTimeString(),
            id: `${data.name}-${data.timestamp || Date.now()}`,
            isTeacher: data.isTeacher || false,
            isLocal: data.name === participantName
        };

        setChatMessages(prev => {
            if (!showChat) {
                setUnreadMessageCount(prev => prev + 1);
            }
            return [...prev, newMessage];
        });
    }, [showChat, participantName]);

    // Send chat message
    const sendChatMessage = async () => {
        if (!chatInput.trim() || !lkRoomRef.current) return;

        const messageText = chatInput.trim();
        
        try {
            const messageData = {
                type: 'chat_message',
                name: participantName,
                message: messageText,
                timestamp: Date.now(),
                isTeacher: userRole === 'teacher'
            };

            console.log('📤 Sending chat message:', messageData);

            // Add message to local chat immediately for better UX
            const localMessage = {
                name: participantName,
                message: messageText,
                time: new Date().toLocaleTimeString(),
                id: `${participantName}-${Date.now()}`,
                isTeacher: userRole === 'teacher',
                isLocal: true
            };

            setChatMessages(prev => [...prev, localMessage]);

            // Convert JSON string to Uint8Array for LiveKit
            const encoder = new TextEncoder();
            const dataToSend = encoder.encode(JSON.stringify(messageData));
            
            console.log('📤 Sending data:', {
                type: messageData.type,
                name: messageData.name,
                message: messageData.message,
                asUint8Array: dataToSend,
                length: dataToSend.length
            });
            
            await lkRoomRef.current.localParticipant.publishData(
                dataToSend,
                { reliable: true }
            );

            console.log('✅ Chat message sent successfully');

            // Clear input
            setChatInput('');
        } catch (error) {
            console.error('Error sending chat message:', error);
            toast.error('Failed to send message');
        }
    };

    // Recording functions
    const stopRecordingIfActive = async () => {
        console.log('🛑 stopRecordingIfActive called', { isRecording, recordingMode, recordingId: recordingIdRef.current });
        if (!isRecording) {
            console.log('⚠️ Not recording, returning early');
            return;
        }
        
        try {
            if (recordingMode === 'cloud' && recordingIdRef.current) {
                // Stop cloud recording
                console.log('☁️ Stopping cloud recording...', { recordingId: recordingIdRef.current });
                
                const response = await axios.post(`${API_ENDPOINT}/api/meet/stopRecording`, {
                    recordingId: recordingIdRef.current
                });
                
                console.log('☁️ Stop recording API response:', response.data);
                
                // Update Firestore recording status
                try {
                    const firestoreDocId = (recordingIdRef as any).firestoreDocId;
                    if (firestoreDocId && tenantId) {
                        const recordingDocRef = doc(db, 'ServiceProviders', tenantId, 'Recordings', firestoreDocId);
                        await updateDoc(recordingDocRef, {
                            status: 'stopped',
                            stopped_at: serverTimestamp(),
                            updated_at: serverTimestamp(),
                            stop_response: response.data?.message || 'Recording stopped successfully'
                        });
                        console.log('✅ Recording status updated in Firestore');
                    } else {
                        console.log('⚠️ No Firestore doc ID found, skipping update');
                    }
                } catch (firestoreError) {
                    console.error('⚠️ Error updating recording status in Firestore:', firestoreError);
                    // Don't fail if Firestore update fails
                }
                
                console.log('☁️ Cloud recording stopped:', response.data);
                
                // Cleanup state for cloud recording
                setIsRecording(false);
                setRecordingMode(null);
                recordingIdRef.current = null;
                setRecordingStartMs(null);
                (recordingIdRef as any).firestoreDocId = null;
                toast.success('☁️ Cloud recording stopped');
            } else if (recordingMode === 'local') {
                // Stop local recording
                console.log('🎥 Stopping local recording...');
                
                // Stop MediaRecorder
                if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                    mediaRecorderRef.current.stop();
                }
                
                // Stop render loop
                if (inAppRecordRafRef.current) {
                    cancelAnimationFrame(inAppRecordRafRef.current);
                    inAppRecordRafRef.current = null;
                }
                // Clear the video check interval
                if ((inAppRecordVideoElsRef as any).checkInterval) {
                    clearInterval((inAppRecordVideoElsRef as any).checkInterval);
                    (inAppRecordVideoElsRef as any).checkInterval = null;
                }
                
                // Cleanup attached tracks
                attachedTrackElsRef.current.forEach(({ track, el }) => {
                    try {
                        track.detach(el);
                        el.remove();
                    } catch (_) {
                        // Ignore cleanup errors
                    }
                });
                attachedTrackElsRef.current = [];
                
                // Cleanup audio context
                if (audioContextRef.current) {
                    try {
                        audioContextRef.current.close();
                    } catch (_) {
                        // Ignore close errors
                    }
                    audioContextRef.current = null;
                }
                
                // Stop all tracks in combined stream
                if (inAppVideoStreamRef.current) {
                    inAppVideoStreamRef.current.getTracks().forEach(track => track.stop());
                    inAppVideoStreamRef.current = null;
                }
                
                // Wait for MediaRecorder to finish - download will trigger in onstop
                console.log('🎥 Local recording stopped - waiting for download in onstop event');
            }
            
            // State cleanup happens after download completes
            // setIsRecording(false);
            // setRecordingMode(null);
            // recordingIdRef.current = null;
            // setRecordingStartMs(null);
            // toast.success('Recording stopped');
        } catch (error) {
            console.error('Error stopping recording:', error);
            toast.error('Failed to stop recording');
        }
    };

    // Parse roomName to extract originalRoomName
    // Format: "tenant-abc-myroom-1730373390123" -> originalRoomName: "myroom"
    const parseOriginalRoomName = (roomName: string): string => {
        // Split by '-' and get all parts except first (tenantId) and last (timestamp)
        const parts = roomName.split('-');
        if (parts.length >= 3) {
            // Remove tenantId (first) and timestamp (last), join the rest
            return parts.slice(1, -1).join('-');
        }
        // Fallback: return roomName without tenant prefix if format is unexpected
        return roomName.replace(/^[^-]+-/, '');
    };

    const startCloudRecording = async () => {
        console.log('🚀 startCloudRecording function called!');
        
        if (!tenantId) {
            console.error('❌ tenantId is missing!');
            toast.error('Missing tenant ID. Cannot start recording.');
            return;
        }
        
        if (!roomName) {
            console.error('❌ roomName is missing!');
            toast.error('Missing room name. Cannot start recording.');
            return;
        }
        
        try {
            const originalRoomName = parseOriginalRoomName(roomName);
            
            console.log('☁️ Starting cloud recording...', { 
                roomName, 
                tenantId,
                originalRoomName,
                participantName,
                API_ENDPOINT
            });
            
            // Build request payload exactly as per API spec
            const requestPayload = {
                roomName: roomName,
                options: {
                    tenantId: tenantId,
                    originalRoomName: originalRoomName,
                    createdBy: participantName || 'teacher'
                }
            };
            
            // Log the exact payload being sent
            console.log('📤 Request payload (EXACT):', JSON.stringify(requestPayload, null, 2));
            console.log('📤 Request payload (structure check):', {
                hasRoomName: !!requestPayload.roomName,
                hasOptions: !!requestPayload.options,
                optionsKeys: requestPayload.options ? Object.keys(requestPayload.options) : [],
                optionsTenantId: requestPayload.options?.tenantId,
                optionsOriginalRoomName: requestPayload.options?.originalRoomName,
                optionsCreatedBy: requestPayload.options?.createdBy
            });
            console.log('📤 API URL:', `${API_ENDPOINT}/api/meet/startRecording`);
            
            // Call API with correct format - DO NOT modify payload
            const response = await axios.post(
                `${API_ENDPOINT}/api/meet/startRecording`, 
                requestPayload, // Send as-is, no transformation
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            console.log('✅ Cloud recording API response:', response.data);
            console.log('✅ Response status:', response.status);
            
            // Extract recording_id from response
            const recordingId = response.data?.recording_id;
            
            if (!recordingId) {
                throw new Error('No recording ID received');
            }
            
            // Store recording data in Firestore
            try {
                const recordingsRef = collection(db, 'ServiceProviders', tenantId, 'Recordings');
                const recordingDocRef = await addDoc(recordingsRef, {
                    recording_id: recordingId,
                    room_name: roomName,
                    status: response.data?.status || 'active',
                    filename: response.data?.filename || null,
                    s3_bucket: response.data?.s3_bucket || null,
                    s3_key: response.data?.s3_key || null,
                    started_at: response.data?.started_at || serverTimestamp(),
                    tenant_id: tenantId,
                    original_room_name: originalRoomName,
                    created_by: participantName || 'teacher',
                    meeting_title: meetingTitle,
                    recording_type: 'cloud',
                    resolution: selectedCloudRes,
                    layout: recordLayout,
                    created_at: serverTimestamp(),
                    updated_at: serverTimestamp()
                });
                
                console.log('✅ Recording saved to Firestore:', recordingDocRef.id);
                
                // Store Firestore document ID for potential future updates
                (recordingIdRef as any).firestoreDocId = recordingDocRef.id;
            } catch (firestoreError) {
                console.error('⚠️ Error saving recording to Firestore:', firestoreError);
                // Don't fail the recording if Firestore save fails
            }
            
            recordingIdRef.current = recordingId;
            setIsRecording(true);
            setRecordingMode('cloud');
            setRecordingStartMs(Date.now());
            toast.success(`☁️ Cloud recording started (${selectedCloudRes})`);
            console.log('☁️ Cloud recording started successfully:', recordingId);
        } catch (error: any) {
            console.error('❌ Error starting cloud recording:', error);
            console.error('❌ Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                statusText: error.response?.statusText,
                config: {
                    url: error.config?.url,
                    method: error.config?.method,
                    data: error.config?.data
                },
                stack: error.stack
            });
            
            const errorMsg = error.response?.data?.error || 
                           error.response?.data?.message || 
                           error.message || 
                           'Failed to start cloud recording';
            toast.error(`☁️ ${errorMsg}`);
            
            // Re-throw so caller can handle if needed
            throw error;
        }
    };

    // Helper function for smart crop
    const getSmartCrop = async (videoEl: HTMLVideoElement, targetW: number, targetH: number) => {
        // Simple center crop - can be enhanced with face detection
        const sourceW = videoEl.videoWidth || 1;
        const sourceH = videoEl.videoHeight || 1;
        const sourceAspect = sourceW / sourceH;
        const targetAspect = targetW / targetH;

        let cropW = sourceW;
        let cropH = sourceH;
        let cropX = 0;
        let cropY = 0;

        if (sourceAspect > targetAspect) {
            // Source is wider - crop width
            cropW = sourceH * targetAspect;
            cropX = (sourceW - cropW) / 2;
        } else {
            // Source is taller - crop height
            cropH = sourceW / targetAspect;
            cropY = (sourceH - cropH) / 2;
        }

        return { x: cropX, y: cropY, w: cropW, h: cropH };
    };

    const startLocalRecording = async () => {
        try {
            if (!lkRoomRef.current) {
                throw new Error('Room not connected');
            }

            // REMOVED: Dark overlay prevention - no longer hiding controls during recording

            // Build in-app recording sources - focus on local participant (instructor) camera + screen share
            let localCameraVideo: HTMLVideoElement | null = null;
            let localScreenShareVideo: HTMLVideoElement | null = null;
            const lkRoom = lkRoomRef.current;

            // Get local participant's camera and screen share tracks
            // For local participant, we want their published tracks (what they're broadcasting)
            if (lkRoom && lkRoom.localParticipant) {
                try {
                    const localParticipant = lkRoom.localParticipant;
                    
                    // Get published tracks from local participant
                    // LiveKit uses trackPublications (Map) for published tracks
                    const videoTrackPublications: any[] = [];
                    
                    // Method 1: Get from trackPublications (Map)
                    if (localParticipant.trackPublications) {
                        const trackPubs = localParticipant.trackPublications.values ? 
                            Array.from(localParticipant.trackPublications.values()) : 
                            (Array.isArray(localParticipant.trackPublications) ? localParticipant.trackPublications : []);
                        videoTrackPublications.push(...trackPubs);
                    }
                    
                    // Method 2: Get from videoTrackPublications (if available)
                    if (localParticipant.videoTrackPublications) {
                        const videoPubs = localParticipant.videoTrackPublications.values ? 
                            Array.from(localParticipant.videoTrackPublications.values()) : 
                            (Array.isArray(localParticipant.videoTrackPublications) ? localParticipant.videoTrackPublications : []);
                        videoTrackPublications.push(...videoPubs);
                    }
                    
                    // Method 3: Get from videoTracks (if available)
                    if (localParticipant.videoTracks) {
                        const videoTracks = localParticipant.videoTracks.values ? 
                            Array.from(localParticipant.videoTracks.values()) : 
                            (Array.isArray(localParticipant.videoTracks) ? localParticipant.videoTracks : []);
                        videoTrackPublications.push(...videoTracks);
                    }
                    
                    console.log('🎥 Local participant video tracks found:', videoTrackPublications.length);
                    
                    for (const pub of videoTrackPublications) {
                        // Get the actual track
                        const track = pub?.track || pub?.trackPublication?.track || pub;
                        if (!track || track.kind !== 'video') continue;
                        
                        // Determine source type - check multiple possible locations
                        const source = pub.source || pub.trackPublication?.source || track.source || '';
                        const src = source.toString().toLowerCase();
                        
                        // Strict check for screen share - must be explicitly 'screen_share' or 'screen'
                        const isScreenShare = source === 'screen_share' || 
                                            source === 'screen' || 
                                            src === 'screen_share' || 
                                            src === 'screen';
                        
                        // Camera is anything that's NOT screen share
                        const isCamera = !isScreenShare && (source === 'camera' || source === '' || !source);
                        
                        console.log(`🎥 Processing track: source="${source}", src="${src}", isScreenShare=${isScreenShare}, isCamera=${isCamera}`, {
                            pubSource: pub.source,
                            trackSource: track.source,
                            trackName: pub.trackName || track.name
                        });
                        
                        // Skip if we already have this type
                        if (isScreenShare && localScreenShareVideo) {
                            console.log('⏭️ Skipping - already have screen share');
                            continue;
                        }
                        if (isCamera && localCameraVideo) {
                            console.log('⏭️ Skipping - already have camera');
                            continue;
                        }
                        
                                const el = document.createElement('video');
                                el.muted = true;
                                (el as any).playsInline = true;
                                el.autoplay = true;
                        el.playsInline = true;
                                try {
                            track.attach(el);
                                    void el.play?.();
                            
                            // Wait a bit for the video to start
                            await new Promise(r => setTimeout(r, 200));
                            
                            // Verify video is actually playing
                            if (el.videoWidth === 0 || el.videoHeight === 0) {
                                console.warn('⚠️ Video element has no dimensions, waiting more...');
                                await new Promise(r => setTimeout(r, 300));
                            }
                            
                            if (isScreenShare && !localScreenShareVideo && el.videoWidth > 0) {
                                // Screen share - store reference
                                localScreenShareVideo = el;
                                attachedTrackElsRef.current.push({ track, el });
                                console.log('✅ Captured screen share track:', {
                                    width: el.videoWidth,
                                    height: el.videoHeight,
                                    source: source
                                });
                            } else if (isCamera && !localCameraVideo && el.videoWidth > 0) {
                                // Camera feed - store reference
                                localCameraVideo = el;
                                attachedTrackElsRef.current.push({ track, el });
                                console.log('✅ Captured camera track:', {
                                    width: el.videoWidth,
                                    height: el.videoHeight,
                                    source: source
                                });
                                    } else {
                                console.warn('⚠️ Track not captured:', {
                                    isScreenShare,
                                    isCamera,
                                    hasScreenShare: !!localScreenShareVideo,
                                    hasCamera: !!localCameraVideo,
                                    videoWidth: el.videoWidth
                                });
                            }
                        } catch (error) {
                            console.error('Error attaching track:', error);
                        }
                    }
                } catch (error) {
                    console.error('Error getting local participant tracks:', error);
                                    }
            }

            // Fallback to DOM videos if track-based collection didn't work
            // Try to get videos from the DOM where LiveKit VideoConference renders them
            if (!localCameraVideo || !localScreenShareVideo) {
                console.log('🔍 Fallback: Searching DOM for videos...', {
                    hasCamera: !!localCameraVideo,
                    hasScreenShare: !!localScreenShareVideo
                });
                
                const containerEl = meetingRootRef.current || document.body;
                
                // Get all video elements in the container
                const allVideos = Array.from(containerEl.querySelectorAll('video')) as HTMLVideoElement[];
                console.log('🔍 Found', allVideos.length, 'video elements in DOM');
                
                // Log all videos for debugging
                allVideos.forEach((v, i) => {
                    const src = v.getAttribute('data-lk-source') || '';
                    const isLocal = v.getAttribute('data-local') === 'true';
                    console.log(`🔍 Video ${i}:`, {
                        source: src,
                        isLocal,
                        width: v.videoWidth,
                        height: v.videoHeight,
                        paused: v.paused,
                        classes: Array.from(v.classList),
                        parentClasses: v.parentElement ? Array.from(v.parentElement.classList) : []
                    });
                });
                
                // First pass: Find screen share videos - check multiple ways
                if (!localScreenShareVideo) {
                    for (const v of allVideos) {
                        if ((v.videoWidth || 0) === 0 || v.paused) continue;
                        
                        // Check multiple ways to identify screen share
                        const src = (v.getAttribute('data-lk-source') || '').toLowerCase();
                        const parent = v.closest('.lk-screen-share-tile, .screen-share-video, .screen-share-layout, .screen-area');
                        const hasScreenShareClass = v.classList.contains('lk-screen-share') || 
                                                   v.parentElement?.classList.contains('lk-screen-share-tile') ||
                                                   v.parentElement?.classList.contains('screen-share-video');
                        const isScreenShare = src === 'screen_share' || 
                                            src === 'screen' ||
                                            src.includes('screen_share') ||
                                            parent !== null ||
                                            hasScreenShareClass;
                        
                        console.log(`🔍 Checking video for screen share:`, {
                            src,
                            hasParent: !!parent,
                            hasClass: hasScreenShareClass,
                            isScreenShare,
                            width: v.videoWidth,
                            height: v.videoHeight
                        });
                        
                        if (isScreenShare) {
                            localScreenShareVideo = v;
                            console.log('✅ Found screen share video in DOM:', {
                                video: v,
                                width: v.videoWidth,
                                height: v.videoHeight,
                                source: src
                            });
                            break;
                        }
                    }
                }
                
                // Second pass: Find local participant's camera video
                if (!localCameraVideo) {
                    for (const v of allVideos) {
                        if ((v.videoWidth || 0) === 0 || v.paused) continue;
                        
                        // Skip screen share videos
                        const src = (v.getAttribute('data-lk-source') || '').toLowerCase();
                        if (src.includes('screen') || src.includes('screen_share')) continue;
                        
                        // Check if this is the local participant's camera video
                        const isLocal = v.getAttribute('data-local') === 'true' ||
                                      v.closest('[data-local="true"]') !== null ||
                                      v.closest('.custom-video-tile') !== null;
                        
                        // Also check if it's in a video tile that's not screen share
                        const parentTile = v.closest('.custom-video-tile, .lk-participant-tile');
                        const isInParticipantTile = parentTile !== null;
                        
                        if (isLocal || (isInParticipantTile && !src.includes('screen'))) {
                            localCameraVideo = v;
                            console.log('📹 Found local camera video in DOM:', v);
                            break;
                        }
                    }
                }
                
                // Third pass: If still missing camera, try broader search
                if (!localCameraVideo) {
                    const candidateVideos = allVideos.filter(v => {
                        if ((v.videoWidth || 0) === 0 || v.paused) return false;
                        const src = (v.getAttribute('data-lk-source') || '').toLowerCase();
                        return !src.includes('screen') && !src.includes('screen_share');
                    });
                    
                    if (candidateVideos.length > 0) {
                        // Prefer videos that are not in screen share containers
                        const nonScreenShareVideos = candidateVideos.filter(v => {
                            const parent = v.closest('.lk-screen-share-tile, .screen-share-video, .screen-share-layout');
                            return !parent;
                        });
                        
                        localCameraVideo = (nonScreenShareVideos.length > 0 ? nonScreenShareVideos[0] : candidateVideos[0]);
                        console.log('📹 Found camera video (fallback):', localCameraVideo);
                    }
                }
            }

            // Wait for videos to be ready and validate they're working
            if (localCameraVideo || localScreenShareVideo) {
                await new Promise(r => setTimeout(r, 300));
                
                // Validate video elements are working
                if (localCameraVideo && ((localCameraVideo.videoWidth || 0) === 0 || localCameraVideo.paused)) {
                    console.warn('⚠️ Camera video not ready, retrying...');
                    // Give it more time
                    await new Promise(r => setTimeout(r, 200));
                }
                
                if (localScreenShareVideo && ((localScreenShareVideo.videoWidth || 0) === 0 || localScreenShareVideo.paused)) {
                    console.warn('⚠️ Screen share video not ready, retrying...');
                    // Give it more time
                    await new Promise(r => setTimeout(r, 200));
                }
            }

            console.log('🎥 Final video collection:', {
                camera: localCameraVideo ? {
                    width: localCameraVideo.videoWidth,
                    height: localCameraVideo.videoHeight,
                    paused: localCameraVideo.paused,
                    readyState: localCameraVideo.readyState
                } : null,
                screenShare: localScreenShareVideo ? {
                    width: localScreenShareVideo.videoWidth,
                    height: localScreenShareVideo.videoHeight,
                    paused: localScreenShareVideo.paused,
                    readyState: localScreenShareVideo.readyState
                } : null
            });

            // Prepare video array based on layout and available videos
            // For instructor recording: screen share + camera, honoring the selected layout
            const finalVideos: HTMLVideoElement[] = [];
            
            // Warn if we're missing one of the videos
            if (localScreenShareVideo && !localCameraVideo) {
                console.warn('⚠️ Only screen share found, camera feed not detected. Recording will show only screen share.');
                toast.warning('Camera feed not detected. Recording will show only screen share.');
            } else if (!localScreenShareVideo && localCameraVideo) {
                console.warn('⚠️ Only camera found, screen share not detected. Recording will show only camera.');
                toast.warning('Screen share not detected. Recording will show only camera.');
            }
            
            if (localScreenShareVideo && localCameraVideo) {
                // Both screen share and camera available - combine based on layout
                console.log('✅ Both videos found - combining based on layout:', recordLayout);
                if (recordLayout === 'pip-circle') {
                    // PiP Circle: screen share as main (full screen), camera as PiP overlay (bottom-right)
                    finalVideos.push(localScreenShareVideo, localCameraVideo);
                } else if (recordLayout === 'side-by-side') {
                    // Side-by-side: screen share on left, camera on right (50/50 split)
                    finalVideos.push(localScreenShareVideo, localCameraVideo);
                } else {
                    // Auto: screen share as main, camera as secondary (grid layout)
                    finalVideos.push(localScreenShareVideo, localCameraVideo);
            }
            } else if (localScreenShareVideo) {
                // Only screen share available - use it as main
                console.log('⚠️ Only screen share available');
                finalVideos.push(localScreenShareVideo);
            } else if (localCameraVideo) {
                // Only camera available - use it as main
                console.log('⚠️ Only camera available');
                finalVideos.push(localCameraVideo);
            } else {
                toast.error('No active video to record');
                return;
            }

            console.log('🎥 Final recording setup:', {
                layout: recordLayout,
                screenShare: !!localScreenShareVideo,
                camera: !!localCameraVideo,
                videoCount: finalVideos.length,
                finalVideos: finalVideos.map((v, i) => ({
                    index: i,
                    width: v.videoWidth,
                    height: v.videoHeight,
                    paused: v.paused
                }))
            });

            inAppRecordVideoElsRef.current = finalVideos;

            // Function to dynamically update video array when screen share starts during recording
            const updateVideoArray = () => {
                const currentCamera = localCameraVideo;
                let currentScreenShare = localScreenShareVideo;
                
                // Check for new tracks from local participant
                if (lkRoom && lkRoom.localParticipant) {
                    try {
                        const localParticipant = lkRoom.localParticipant;
                        const videoTrackPublications: any[] = [];
                        
                        if (localParticipant.trackPublications) {
                            const trackPubs = localParticipant.trackPublications.values ? 
                                Array.from(localParticipant.trackPublications.values()) : 
                                (Array.isArray(localParticipant.trackPublications) ? localParticipant.trackPublications : []);
                            videoTrackPublications.push(...trackPubs);
            }
                        
                        for (const pub of videoTrackPublications) {
                            const track = pub?.track || pub?.trackPublication?.track || pub;
                            if (!track || track.kind !== 'video') continue;
                            
                            const source = pub.source || pub.trackPublication?.source || track.source || '';
                            const src = source.toString().toLowerCase();
                            const isScreenShare = source === 'screen_share' || source === 'screen' || 
                                                src === 'screen_share' || src === 'screen';
                            
                            // If we found a screen share track and don't have one yet, create a video element for it
                            if (isScreenShare && !currentScreenShare) {
                                const el = document.createElement('video');
                                el.muted = true;
                                (el as any).playsInline = true;
                                el.autoplay = true;
                                try {
                                    track.attach(el);
                                    void el.play?.();
                                    currentScreenShare = el;
                                    attachedTrackElsRef.current.push({ track, el });
                                    console.log('✅ Screen share detected during recording!');
                                } catch (e) {
                                    console.error('Error attaching screen share track during recording:', e);
                                }
                            }
                        }
                    } catch (e) {
                        console.error('Error checking for new tracks:', e);
                    }
                }
                
                // Also check DOM for new screen share videos
                if (!currentScreenShare) {
                    const containerEl = meetingRootRef.current || document.body;
                    const allVideos = Array.from(containerEl.querySelectorAll('video')) as HTMLVideoElement[];
                    
                    for (const v of allVideos) {
                        if ((v.videoWidth || 0) === 0 || v.paused) continue;
                        
                        const src = (v.getAttribute('data-lk-source') || '').toLowerCase();
                        const parent = v.closest('.lk-screen-share-tile, .screen-share-video, .screen-share-layout, .screen-area');
                        const isScreenShare = src === 'screen_share' || src === 'screen' ||
                                            src.includes('screen_share') || parent !== null;
                        
                        if (isScreenShare && !currentScreenShare) {
                            currentScreenShare = v;
                            console.log('✅ Screen share video found in DOM during recording!');
                            break;
                        }
                    }
                }
                
                // Update video array if we found a new screen share
                if (currentScreenShare && currentScreenShare !== localScreenShareVideo) {
                    // Update the local reference
                    localScreenShareVideo = currentScreenShare;
                    const updatedVideos: HTMLVideoElement[] = [];
                    
                    // Use the actual camera reference
                    const actualCamera = localCameraVideo;
                    
                    if (currentScreenShare && actualCamera) {
                        // Both available - arrange based on layout
                        // Always put screen share first, camera second
                        updatedVideos.push(currentScreenShare, actualCamera);
                        console.log('✅ Updated recording: Both screen share and camera now available!');
                    } else if (currentScreenShare) {
                        updatedVideos.push(currentScreenShare);
                        console.log('✅ Updated recording: Screen share added!');
                    } else if (actualCamera) {
                        updatedVideos.push(actualCamera);
                    }
                    
                    if (updatedVideos.length > 0) {
                        inAppRecordVideoElsRef.current = updatedVideos;
                        console.log('✅ Recording video array updated:', {
                            screenShare: !!currentScreenShare,
                            camera: !!actualCamera,
                            count: updatedVideos.length,
                            layout: recordLayout
                        });
                    }
                }
            };

            // Setup canvas per selected local resolution
            const { width, height } = selectedLocalRes === '1080p' 
                ? { width: 1920, height: 1080 } 
                : { width: 1280, height: 720 };

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            inAppCanvasRef.current = canvas;
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('Canvas 2D context not available');

            // Track check interval for dynamic video detection
            let checkCount = 0;
            const checkInterval = setInterval(() => {
                checkCount++;
                // Check every 30 frames (~0.5 seconds at 60fps) for new videos
                if (checkCount >= 30) {
                    checkCount = 0;
                    updateVideoArray();
                }
            }, 1000 / 60); // Check at ~60fps

            // Store interval for cleanup
            (inAppRecordVideoElsRef as any).checkInterval = checkInterval;

            // Render loop - synchronous
            const render = () => {
                ctx.clearRect(0, 0, width, height);
                const tiles = inAppRecordVideoElsRef.current;
                const nTiles = tiles.length;

                // Debug logging
                if (Math.random() < 0.01) { // Log occasionally to avoid spam
                    console.log('🎬 Render loop:', {
                        layout: recordLayout,
                        nTiles,
                        tile0: tiles[0] ? { width: tiles[0].videoWidth, height: tiles[0].videoHeight } : null,
                        tile1: tiles[1] ? { width: tiles[1].videoWidth, height: tiles[1].videoHeight } : null
                    });
                }

                // Render based on selected layout
                if (recordLayout === 'pip-circle') {
                    if (nTiles >= 2) {
                        // PiP Circle with both videos: Screen share as main, camera as PiP overlay
                        const screenShare = tiles[0]; // First video should be screen share
                        const camera = tiles[1]; // Second video should be camera
                        
                        try { 
                            // Draw screen share full screen
                            ctx.drawImage(screenShare, 0, 0, width, height); 
                        } catch (e) { 
                            console.error('Error drawing screen share:', e);
                        }
                        
                        // Draw camera as circular PiP overlay (top-right corner, matching UI layout)
                        const camSize = Math.floor(Math.min(width, height) * 0.22);
                        const pad = Math.floor(camSize * 0.1);
                        const x = width - camSize - pad; // Right side
                        const y = pad; // Top side (changed from bottom)
                        
                        try {
                            ctx.save();
                            ctx.beginPath();
                            ctx.arc(x + camSize / 2, y + camSize / 2, camSize / 2, 0, Math.PI * 2);
                            ctx.clip();
                            ctx.drawImage(camera, x, y, camSize, camSize);
                            ctx.restore();
                        } catch (e) { 
                            console.error('Error drawing camera PiP:', e);
                        }
                    } else if (nTiles >= 1) {
                        // PiP Circle with only one video: render full screen
                        try {
                            ctx.drawImage(tiles[0], 0, 0, width, height);
                        } catch (e) {
                            console.error('Error drawing single video:', e);
                        }
                    }
                } else if (recordLayout === 'side-by-side') {
                    if (nTiles >= 2) {
                        // Side-by-side: Screen share on left, camera on right (50/50 split)
                    const wHalf = Math.floor(width / 2);
                        try { 
                            ctx.drawImage(tiles[0], 0, 0, wHalf, height); // Left: screen share
                        } catch (e) {
                            console.error('Error drawing left video:', e);
                        }
                    try {
                            ctx.drawImage(tiles[1], wHalf, 0, width - wHalf, height); // Right: camera
                        } catch (e) {
                            console.error('Error drawing right video:', e);
                        }
                    } else if (nTiles >= 1) {
                        // Side-by-side with only one video: render full screen
                        try {
                            ctx.drawImage(tiles[0], 0, 0, width, height);
                        } catch (e) {
                            console.error('Error drawing single video:', e);
                        }
                    }
                } else {
                    // Auto/grid layout: arrange videos in a grid
                    const n = tiles.length;
                    if (n === 1) {
                        // Single video: full screen
                        try {
                            ctx.drawImage(tiles[0], 0, 0, width, height);
                        } catch (e) {
                            console.error('Error drawing single video:', e);
                        }
                    } else if (n >= 2) {
                        // Multiple videos: grid layout
                    const cols = Math.ceil(Math.sqrt(n));
                    const rows = Math.ceil(n / cols);
                    const cellW = Math.floor(width / cols);
                    const cellH = Math.floor(height / rows);

                    for (let i = 0; i < n; i++) {
                        const v = tiles[i];
                        if (!v) continue;
                        const r = Math.floor(i / cols);
                        const c = i % cols;
                        const dx = c * cellW;
                        const dy = r * cellH;
                        try {
                            ctx.drawImage(v, dx, dy, cellW, cellH);
                            } catch (e) {
                                console.error(`Error drawing video ${i}:`, e);
                            }
                        }
                    }
                }
                inAppRecordRafRef.current = requestAnimationFrame(render);
            };
            inAppRecordRafRef.current = requestAnimationFrame(render);

            // Audio mixing
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            audioContextRef.current = audioContext;
            const destination = audioContext.createMediaStreamDestination();
            audioDestinationRef.current = destination;

            const container = meetingRootRef.current || document.body;
            const audioEls = Array.from(container.querySelectorAll('audio')) as HTMLAudioElement[];
            audioEls.forEach(el => {
                try {
                    const src = audioContext.createMediaElementSource(el);
                    src.connect(destination);
                } catch (e) {
                    // Ignore duplicates
                }
            });

            try {
                const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
                const micSrc = audioContext.createMediaStreamSource(mic);
                micSrc.connect(destination);
            } catch (e) {
                // mic optional
            }

            // Build combined stream
            const videoStream = canvas.captureStream(30);
            inAppVideoStreamRef.current = videoStream;
            const combined = new MediaStream([
                ...videoStream.getVideoTracks(),
                ...destination.stream.getAudioTracks()
            ]);

            // Choose best available mime type
            const mimeCandidates = [
                'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
                'video/mp4',
                'video/webm;codecs=vp9,opus',
                'video/webm'
            ];
            let selectedMime: string | undefined;
            let selectedExt: 'mp4' | 'webm' = 'webm';
            
            if (typeof MediaRecorder !== 'undefined' && (MediaRecorder as any).isTypeSupported) {
                for (const cand of mimeCandidates) {
                    if ((MediaRecorder as any).isTypeSupported(cand)) {
                        selectedMime = cand;
                        selectedExt = cand.includes('mp4') ? 'mp4' : 'webm';
                        break;
                    }
                }
            }
            
            localRecordingExtRef.current = selectedExt;
            if (selectedExt === 'webm') {
                toast.info('Recording as WEBM format');
            }

            const mediaRecorder = selectedMime
                ? new MediaRecorder(combined, { mimeType: selectedMime })
                : new MediaRecorder(combined);
            mediaRecorderRef.current = mediaRecorder;
            recordedChunksRef.current = [];
            
            mediaRecorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) {
                    console.log('📦 Recording data chunk:', e.data.size, 'bytes');
                    recordedChunksRef.current.push(e.data);
                }
            };
            
            mediaRecorder.onstop = async () => {
                console.log('🛑 MediaRecorder stopped, total chunks:', recordedChunksRef.current.length);
                
                // Cleanup state first to remove overlay
                setIsRecording(false);
                setRecordingMode(null);
                recordingIdRef.current = null;
                setRecordingStartMs(null);
                
                // Create and download the file
                if (recordedChunksRef.current.length > 0) {
                    const blob = new Blob(recordedChunksRef.current, { type: `video/${localRecordingExtRef.current}` });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `recording-${Date.now()}.${localRecordingExtRef.current}`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    toast.success('Recording downloaded');
                    console.log('✅ File downloaded successfully');
                } else {
                    console.warn('⚠️ No data chunks recorded');
                    toast.error('No recording data to save');
                }
            };
            
            mediaRecorder.start(1000); // Collect data every 1 second
            recordingIdRef.current = 'local'; // Set ID so stop function knows it's recording
            setRecordingMode('local');
            setIsRecording(true);
            setRecordingStartMs(Date.now());
            toast.success('Local recording started');
        } catch (err) {
            console.error('Failed to start local recording:', err);
            toast.error('Failed to start local recording');
        }
    };

    // Meeting hours tracking - Only track teacher minutes
    const startMeetingHoursTracking = async () => {
        if (userRole !== 'teacher') return;
        
        try {
            const meetingRef = doc(db, 'meetings', `${tenantId}_${roomName}`);
            await setDoc(meetingRef, {
                startTime: serverTimestamp(),
                tenantId,
                roomName,
                status: 'active',
                teacherPresent: true,
                teacherStartTime: serverTimestamp()
            }, { merge: true });
            
            // Store the current hours balance at meeting start for real-time calculation
            const currentHours = subscriptionHoursRemaining;
            setInitialHoursAtMeetingStart(currentHours);
            setMeetingStartTime(Date.now());
            
            console.log('📊 Teacher meeting hours tracking started', {
                initialHours: currentHours,
                startTime: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error starting meeting tracking:', error);
        }
    };

    const stopMeetingHoursTracking = async () => {
        if (userRole !== 'teacher' || !meetingStartTime || !tenantId) return;
        
        try {
            const duration = (Date.now() - meetingStartTime) / (1000 * 60 * 60); // hours
            
            // Save meeting to top-level meetings collection
            if (duration > 0) {
                try {
                    await addDoc(collection(db, 'meetings'), {
                        serviceProviderId: tenantId,
                        roomName: roomName,
                        meetingTitle: meetingTitle,
                        subscriptionId: subscriptionId || null,
                        subscriptionName: subscriptionName || null,
                        startTime: new Date(meetingStartTime),
                endTime: serverTimestamp(),
                duration: duration,
                status: 'ended',
                        createdAt: serverTimestamp()
                    });
                    console.log('📝 Meeting logged to meetings collection');
                } catch (meetingError) {
                    console.error('Error saving meeting to collection:', meetingError);
                }
            }
            
            // Deduct hours from serviceProviders.meetPlusHours (with 5hr trial for new users)
            if (duration > 0) {
                try {
                    const serviceProviderRef = doc(db, 'serviceProviders', tenantId);
                    const serviceProviderSnap = await getDoc(serviceProviderRef);
                    
                    if (serviceProviderSnap.exists()) {
                        const data = serviceProviderSnap.data();
                        let meetPlusHours = data.meetPlusHours || 0;
                        
                        // If no hours configured, grant 5 trial hours
                        if (meetPlusHours === 0 || meetPlusHours === null || meetPlusHours === undefined) {
                            meetPlusHours = 5;
                            console.log('🎁 Granting 5 trial hours for new service provider');
                            toast.info('🎁 You received 5 trial hours!');
                        }
                        
                        const remaining = meetPlusHours - duration;
                        
                        // Update serviceProvider with meetPlusHours (deduct meeting duration)
                        await updateDoc(serviceProviderRef, {
                            meetPlusHours: Math.max(0, remaining), // Don't go below 0
                            lastUpdated: serverTimestamp()
                        });
                        
                        // Update local state
                        setSubscriptionHoursRemaining(Math.max(0, remaining));
                        setInitialHoursAtMeetingStart(null); // Clear initial hours after meeting ends
                        
                        console.log('📊 Hours deducted from service provider (teacher only):', {
                            meetPlusHours: meetPlusHours.toFixed(2),
                            duration: duration.toFixed(2),
                            remaining: remaining.toFixed(2)
                        });
                        
                        // Warn if running low
                        if (remaining <= 1 && remaining > 0) {
                            toast.warning(`⚠️ Only ${remaining.toFixed(1)} hours remaining! Please purchase more hours.`);
                        } else if (remaining <= 0) {
                            toast.error('❌ Meeting hours exhausted! Please purchase more hours.');
                            setShowPurchaseHoursDialog(true);
                        }
                    }
                } catch (spError) {
                    console.error('Error updating service provider hours:', spError);
                }
            }
            
            console.log('📊 Teacher meeting hours tracking stopped - Duration:', duration.toFixed(2), 'hours');
        } catch (error) {
            console.error('Error stopping meeting tracking:', error);
        }
    };

    // Handle teacher/student leaving - seamless exit
    const handleTeacherLeave = async () => {
        // Mark as intentional disconnect to prevent error toasts
        isIntentionalDisconnectRef.current = true;
        
        try {
            // Stop recording if active
            await stopRecordingIfActive();

            // Cleanup video tracks
            if (lkRoomRef.current?.localParticipant) {
                const videoTrack = lkRoomRef.current.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
                const screenTrack = lkRoomRef.current.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track;
                
                if (videoTrack) {
                    await videoTrack.stop();
                    console.log('📹 Video track stopped');
                }
                if (screenTrack) {
                    await screenTrack.stop();
                    console.log('🖥️ Screen share track stopped');
                }
            }

            if (userRole === 'teacher') {
                const shouldEndAll = confirm('Would you like to end the meeting for all participants?');
                
                if (shouldEndAll) {
                    try {
                        // End meeting for all participants (optional API call)
                        try {
                            await axios.post(`${API_ENDPOINT}/api/meet/endRoom`, {
                                roomName,
                                tenantId
                            });
                            console.log('✅ Meeting ended via API');
                        } catch (apiError) {
                            console.warn('⚠️ endRoom API not available, continuing with local cleanup:', apiError);
                        }
                        
                        // Stop meeting hours tracking
                        await stopMeetingHoursTracking();
                        
                        toast.success('Meeting ended for all participants');
                    } catch (error) {
                        console.error('Error ending meeting:', error);
                        // Don't show error toast on intentional exit
                    }
                } else {
                    // Just leave without ending for others
                    await stopMeetingHoursTracking();
                }
            } else {
                // Student leaving - just stop tracking
                await stopMeetingHoursTracking();
            }

            // Disconnect from room gracefully
            if (lkRoomRef.current) {
                try {
                await lkRoomRef.current.disconnect();
                console.log('🔌 Disconnected from LiveKit room');
                } catch (disconnectError) {
                    console.warn('Disconnect warning (non-critical):', disconnectError);
                    // Continue with cleanup even if disconnect throws
                }
            }

            // Call onDisconnect callback - no error toasts since this is intentional
            onDisconnect(true);
        } catch (error) {
            console.error('Error leaving meeting:', error);
            // Don't show error toast on intentional exit
            // Force disconnect anyway
            onDisconnect(true);
        }
    };

    // Control visibility management - DISABLED to prevent dark overlay
    // Controls are now always visible
    const showControlsTemporarily = useCallback(() => {
        // Do nothing - controls are always visible
        setShowControls(true);
    }, []);

    const handleMouseMove = useCallback(() => {
        // Do nothing - no auto-hide to prevent dark overlay
    }, []);

    // Mirror toggle
    const toggleMirror = useCallback(() => {
        const newMirrored = !isMirrored;
        setIsMirrored(newMirrored);
        localStorage.setItem('meetplus_mirror_video', newMirrored.toString());
        console.log(`🔄 Video mirror ${newMirrored ? 'enabled' : 'disabled'}`);
    }, [isMirrored]);

    // Exclusive panel management - closes other panels and toggles the requested one
    const openExclusivePanel = (panelType: string) => {
        console.log(`🔧 Opening exclusive panel: ${panelType}`);
        
        // Check if the requested panel is already open - if so, close it
        let shouldOpen = true;
        switch (panelType) {
            case 'chat':
                if (showChat) {
                    shouldOpen = false;
                    setShowChat(false);
                }
                break;
            case 'background':
                if (showBackgroundEffects) {
                    shouldOpen = false;
                    setShowBackgroundEffects(false);
                }
                break;
            case 'chess':
                if (showChessBoard) {
                    shouldOpen = false;
                    setShowChessBoard(false);
                }
                break;
            case 'polls':
                if (showPollsQuizzes) {
                    shouldOpen = false;
                    setShowPollsQuizzes(false);
                }
                break;
            case 'meetingInfo':
                if (showMeetingInfo) {
                    shouldOpen = false;
                    setShowMeetingInfo(false);
                }
                break;
        }
        
        // If we're closing the panel, don't do anything else
        if (!shouldOpen) {
            console.log(`🔧 Closing panel: ${panelType}`);
            return;
        }
        
        // Close all other panels first
        setShowChat(false);
        setShowChessBoard(false);
        setShowPollsQuizzes(false);
        setShowBackgroundEffects(false);
        setShowMeetingInfo(false);
        setChatExpanded(false); // Reset chat expansion

        // Then open the requested panel
        switch (panelType) {
            case 'chat':
                console.log('💬 Opening chat panel');
                setShowChat(true);
                setUnreadMessageCount(0); // Reset unread count when chat is opened
                break;
            case 'background':
                console.log('🎨 Opening background effects panel');
                setShowBackgroundEffects(true);
                break;
            case 'chess':
                console.log('♟️ Opening chess panel');
                setShowChessBoard(true);
                break;
            case 'polls':
                console.log('📊 Opening polls panel');
                setShowPollsQuizzes(true);
                break;
            case 'meetingInfo':
                console.log('ℹ️ Opening meeting info panel');
                setShowMeetingInfo(true);
                break;
            default:
                console.log(`❓ Unknown panel type: ${panelType}`);
        }
    };

    // Toggle chat expansion
    const toggleChatExpansion = () => {
        setChatExpanded(!chatExpanded);
        console.log('💬 Chat expansion toggled:', !chatExpanded);
    };

    // Initial permissions request
    useEffect(() => {
        const requestPermissions = async () => {
            try {
                // Request permissions first
                const stream = await navigator.mediaDevices.getUserMedia({ 
                    video: { 
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        frameRate: { ideal: 30 }
                    }, 
                    audio: true 
                });
                
                // Stop tracks immediately - just wanted permissions
                stream.getTracks().forEach(track => track.stop());
                console.log('✅ Camera/mic permissions granted');
            } catch (error) {
                console.error('Error requesting permissions:', error);
                toast.error('Failed to access camera/microphone');
            }
        };
        
        requestPermissions();
    }, []);

    // Preserve meeting context: Don't auto-minimize, only minimize when user explicitly wants it
    // The meeting stays connected even when tab is hidden or view changes
    // This ensures meeting context is preserved when switching tabs/views
    useEffect(() => {
        if (!isConnected) return;

        const handleVisibilityChange = () => {
            // Keep meeting connected even when tab is hidden
            // Don't auto-minimize - preserve meeting context
            if (document.hidden) {
                console.log('📱 Tab hidden - meeting stays connected (context preserved)');
            } else {
                console.log('📱 Tab visible - meeting restored');
                // Auto-restore meeting when tab becomes visible again (if it was minimized)
                if (isMinimized && isScreenSharing) {
                    // Optionally auto-restore, or let user restore manually
                    // setIsMinimized(false);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isConnected, isMinimized, isScreenSharing]);

    // Manage idle and max-duration timers; ensure stop recording on unload
    useEffect(() => {
        if (!isConnected) return;

        const handleActivity = () => {
            // Reset idle timer logic would go here
        };
        
        const handleBeforeUnload = () => {
            try {
                if (isRecording && recordingIdRef.current && navigator.sendBeacon) {
                    const blob = new Blob([JSON.stringify({ recordingId: recordingIdRef.current })], { type: 'application/json' });
                    navigator.sendBeacon(`${API_ENDPOINT}/api/meet/stopRecording`, blob);
                }
            } catch (e) {
                // noop
            }
        };

        window.addEventListener('keydown', handleActivity);
        window.addEventListener('click', handleActivity);
        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('keydown', handleActivity);
            window.removeEventListener('click', handleActivity);
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            const idleTimer = idleTimerRef.current;
            const maxTimer = maxTimerRef.current;
            if (idleTimer) clearTimeout(idleTimer);
            if (maxTimer) clearTimeout(maxTimer);
        };
    }, [isConnected, isRecording]);

    // Draggable minimized window state
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const minimizedWindowRef = useRef<HTMLDivElement>(null);

    // Handle dragging for minimized window
    const handleMinimizedMouseDown = (e: React.MouseEvent) => {
        if (!minimizedWindowRef.current) return;
        const rect = minimizedWindowRef.current.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
        setIsDragging(true);
    };

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging) return;
            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;
            
            // Keep window within viewport bounds
            const maxX = window.innerWidth - 360;
            const maxY = window.innerHeight - 200;
            const boundedX = Math.max(0, Math.min(newX, maxX));
            const boundedY = Math.max(0, Math.min(newY, maxY));
            
            setMinimizedPosition({ x: boundedX, y: boundedY });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);

    const meetingContent = (
        <>
            {/* Minimized Picture-in-Picture Window - Shows when user explicitly minimizes during screen sharing */}
            {isMinimized && isScreenSharing && (
                <div
                    ref={minimizedWindowRef}
                    className="fixed z-[10000] bg-gray-800 rounded-lg shadow-2xl border border-gray-700"
                    style={{
                        left: `${minimizedPosition.x}px`,
                        top: `${minimizedPosition.y}px`,
                        width: '360px',
                        height: '200px',
                        cursor: isDragging ? 'grabbing' : 'grab'
                    }}
                    onMouseDown={handleMinimizedMouseDown}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-3 py-2 bg-gray-900 border-b border-gray-700 rounded-t-lg cursor-grab active:cursor-grabbing">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <span className="text-white text-xs font-medium truncate">{meetingTitle}</span>
                        </div>
                        <button
                            onClick={() => setIsMinimized(false)}
                            className="text-gray-400 hover:text-white transition-colors ml-2"
                            title="Restore Meeting"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                            </svg>
                        </button>
                    </div>

                    {/* Video Preview Area */}
                    <div className="relative h-[120px] bg-black overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-gray-500 text-xs text-center">
                                <div className="mb-2">📹</div>
                                <div>Meeting Active</div>
                            </div>
                        </div>
                        {/* Small video preview could go here */}
                    </div>

                    {/* Controls */}
                    <div className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-900 rounded-b-lg">
                        {/* Mic Toggle */}
                        <button
                            onClick={async () => {
                                try {
                                    if (lkRoomRef.current?.localParticipant) {
                                        await lkRoomRef.current.localParticipant.setMicrophoneEnabled(!micEnabled);
                                        setMicEnabled(!micEnabled);
                                    }
                                } catch (error) {
                                    console.error('Error toggling mic:', error);
                                }
                            }}
                            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                micEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                            }`}
                            title={micEnabled ? 'Mute' : 'Unmute'}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                {micEnabled ? (
                                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                                ) : (
                                    <path d="M1 1l22 22M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                                )}
                            </svg>
                        </button>

                        {/* Camera Toggle */}
                        <button
                            onClick={async () => {
                                try {
                                    if (lkRoomRef.current?.localParticipant) {
                                        await lkRoomRef.current.localParticipant.setCameraEnabled(!cameraEnabled);
                                        setCameraEnabled(!cameraEnabled);
                                    }
                                } catch (error) {
                                    console.error('Error toggling camera:', error);
                                }
                            }}
                            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                cameraEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                            }`}
                            title={cameraEnabled ? 'Turn Off Camera' : 'Turn On Camera'}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                {cameraEnabled ? (
                                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                                ) : (
                                    <path d="M1 1l22 22M23 19V8a2 2 0 0 0-2-2h-4l-2-3H9l-2 3H3a2 2 0 0 0-2 2v11"></path>
                                )}
                            </svg>
                        </button>

                        {/* Leave Meeting */}
                        <button
                            onClick={async () => {
                                await handleTeacherLeave();
                            }}
                            className="w-8 h-8 rounded-full flex items-center justify-center bg-red-600 hover:bg-red-700 transition-colors"
                            title="Leave Meeting"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                <polyline points="16 17 21 12 16 7"></polyline>
                                <line x1="21" y1="12" x2="9" y2="12"></line>
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Main Meeting Container - Always rendered to preserve context, hidden visually when minimized */}
            <div 
                className={`bg-gray-900 overflow-hidden shadow-2xl fixed inset-0 z-[9999] rounded-none ${isMinimized ? 'opacity-0 pointer-events-none' : ''}`}
            onMouseMove={handleMouseMove}
                style={isMinimized ? { visibility: 'hidden' } : { visibility: 'visible' }}
        >
                    <LiveKitRoom
                        video={true} // Start with video on by default
                        audio={true} // Start with audio on by default
                    token={token}
                    serverUrl={serverUrl}
                    connect={true}
                    options={{
                        // Basic connection options
                        adaptiveStream: true,
                        dynacast: true,
                        
                        // Video capture settings
                        videoCaptureDefaults: {
                            resolution: {
                                width: 1280,
                                height: 720,
                                frameRate: 30
                            }
                        },
                        
                        // Audio capture settings
                        audioCaptureDefaults: {
                            autoGainControl: true,
                            echoCancellation: true,
                            noiseSuppression: true
                        },
                        
                        // Publishing settings
                        publishDefaults: {
                            simulcast: false,
                            stopMicTrackOnMute: false, // Don't stop tracks on mute
                            videoSimulcastLayers: [],
                            videoCodec: 'vp8',
                            videoEncoding: {
                                maxBitrate: 1_500_000, // Reduced for better stability
                                maxFramerate: 30
                            },
                            screenShareEncoding: {
                                maxBitrate: 2_000_000, // Reduced for better stability
                                maxFramerate: 30
                            },
                            dtx: true,
                            red: true
                        },
                        
                        // Connection behavior
                        disconnectOnPageLeave: false,
                        stopLocalTrackOnUnpublish: false,
                        
                        // Reconnection settings
                        reconnectAttempts: 10, // More retry attempts
                        reconnectBackoffMs: 1000,
                        
                        // Performance settings
                        suspendLocalVideoOnFocus: false,
                        adaptiveStream: {
                            pixelDensity: 1
                        }
                    }}
                onConnected={async (room) => {
                    console.log('🎉 LiveKit connected successfully!');
                    setIsConnected(true);
                    setIsLoading(false);
                    // Reset intentional disconnect flag for new session
                    isIntentionalDisconnectRef.current = false;
                    toast.success(`Connected to ${meetingTitle}`);
                    
                    // Store room reference
                    lkRoomRef.current = room;

                    // Log initial state
                    console.log('🏁 LiveKitRoom initial state:', {
                        userRole,
                        participantName,
                        meetingTitle,
                        roomName: room?.name || 'unknown',
                        roomState: room?.state || 'unknown',
                        participantCount: room?.participants?.size || 0,
                        hasLocalParticipant: !!room?.localParticipant,
                        micEnabled,
                        cameraEnabled,
                        isScreenSharing,
                        currentLayout
                    });
                    
                    // Ensure camera and mic are enabled
                    const enableMedia = async () => {
                        try {
                            if (!room?.localParticipant) {
                                console.log('⚠️ Room or localParticipant not ready yet, skipping media enable');
                                return;
                            }
                            
                            await room.localParticipant.setCameraEnabled(true);
                            await room.localParticipant.setMicrophoneEnabled(true);
                            setCameraEnabled(true);
                            setMicEnabled(true);
                            console.log('✅ Camera and mic enabled on connect');
                        } catch (error) {
                            console.error('Error enabling camera/mic:', error);
                        }
                    };
                    
                    enableMedia();
                    
                    // Enumerate available devices
                    await enumerateDevices();
                    
                    // Check subscription hours and start meeting tracking for teachers
                    if (userRole === 'teacher') {
                        const { canStart: canStartMeeting, hours: remainingHours } = await fetchSubscriptionHours();
                        if (canStartMeeting) {
                            startMeetingHoursTracking();
                            
                            // Show hours remaining banner at meeting start (only if less than 5 hours)
                            if (remainingHours !== null && remainingHours > 0 && remainingHours < 5) {
                                setShowHoursInfoDialog(true);
                                // Auto-hide after 5 seconds
                                setTimeout(() => {
                                    setShowHoursInfoDialog(false);
                                }, 5000);
                            }
                        }
                    }
                    
                    // Restore persisted background effect
                    const saved = localStorage.getItem('meetplus_background_effect');
                    if (saved === 'blur' || saved === 'image' || saved === 'none') {
                        setCurrentEffect(saved as any);
                    }
                    
                    // Debug connection status
                    try {
                        console.log('🔍 Room connection status:', {
                            state: room?.state,
                            name: room?.name,
                            sid: room?.sid,
                            metadata: room?.metadata,
                            serverVersion: room?.serverVersion,
                            numParticipants: room?.participants?.size || 0,
                            localParticipant: room?.localParticipant?.identity
                        });
                    } catch (error) {
                        console.warn('Failed to log room status:', error);
                    }
                }}
                onDisconnected={async (reason?: any) => {
                    console.log('🔌 LiveKit disconnected:', reason);
                    const wasIntentional = isIntentionalDisconnectRef.current;
                    setIsConnected(false);
                    // Don't set isLoading to true during disconnection - only set it during initial connection
                    // This prevents the black overlay from appearing during reconnection attempts
                    
                    // Skip reconnection and error toasts if disconnect was intentional
                    if (wasIntentional) {
                        console.log('✅ Intentional disconnect - exiting gracefully');
                        // Reset flag for next session
                        isIntentionalDisconnectRef.current = false;
                        
                        // Stop recording only on intentional disconnect
                        await stopRecordingIfActive();
                        
                        // Stop meeting hours tracking for teachers
                        if (userRole === 'teacher') {
                            await stopMeetingHoursTracking();
                        }
                        
                        onDisconnect(true);
                        return;
                    }
                    
                    // Try to reconnect only if it wasn't an intentional disconnect and wasn't a client-initiated disconnect
                    if (reason && reason !== 'Client initiated disconnect' && reason.toString().includes('Client') === false && lkRoomRef.current) {
                        console.log('🔄 Attempting to reconnect...');
                        console.log('⏸️ NOT stopping recording - waiting for reconnection...');
                        try {
                            await lkRoomRef.current.connect(serverUrl, token);
                            console.log('✅ Reconnected successfully');
                            toast.success('Reconnected to meeting');
                            // Ensure loading state is false after reconnection to prevent black overlay
                            setIsLoading(false);
                            setIsConnected(true);
                            // Recording continues after reconnection
                        } catch (error) {
                            console.error('❌ Reconnection failed:', error);
                            toast.error('Failed to reconnect');
                            
                            // Only stop recording if reconnection fails permanently
                            console.log('🛑 Reconnection failed - stopping recording...');
                            await stopRecordingIfActive();
                            
                            // Stop meeting hours tracking for teachers
                            if (userRole === 'teacher') {
                                await stopMeetingHoursTracking();
                            }
                            
                            onDisconnect(false);
                        }
                    } else {
                        // Normal disconnect (no reconnection attempt) - stop recording
                        console.log('🛑 Final disconnect - stopping recording...');
                        await stopRecordingIfActive();
                        
                        // Stop meeting hours tracking for teachers
                        if (userRole === 'teacher') {
                            await stopMeetingHoursTracking();
                        }
                        
                        // Normal disconnect - call callback silently
                        onDisconnect(true);
                    }
                }}
                onError={(error) => {
                    // Skip error toasts if disconnect was intentional
                    if (isIntentionalDisconnectRef.current) {
                        console.log('⚠️ LiveKit error during intentional disconnect (silent):', error.message);
                        return;
                    }
                    
                    console.error('LiveKit error:', error);
                    
                    // Handle specific error types - only show toasts for unexpected errors
                    if (error.message?.includes('token')) {
                        toast.error('Invalid meeting token. Please try joining again.');
                    } else if (error.message?.includes('room')) {
                        toast.error('Room not found or expired.');
                    } else if (error.message?.includes('permission')) {
                        toast.error('Permission denied to access camera/microphone.');
                    } else if (error.message?.includes('network')) {
                        toast.error('Network connection issue. Please check your internet.');
                    } else if (error.message?.includes('disconnect') || error.message?.includes('disconnected')) {
                        // Disconnect-related errors are expected during intentional exit
                        console.log('Disconnect-related error (expected during exit):', error.message);
                    } else {
                        toast.error('Failed to connect to meeting. Please try again.');
                    }

                    // Log detailed error info
                    console.log('🔍 Detailed error info:', {
                        name: error.name,
                        message: error.message,
                        stack: error.stack,
                        token: token ? 'present' : 'missing',
                        serverUrl: serverUrl ? 'present' : 'missing',
                        roomName: roomName ? 'present' : 'missing'
                    });

                    if (onError) onError(error);
                }}
                style={{ height: '100vh', width: '100%' }}
            >
                {/* Provide room context to ref collector */}
                <RoomRefCollector onRoom={(room) => (lkRoomRef.current = room)} />
                <div className="flex flex-col h-full">
                    {/* Compact Top Bar - Dark Theme */}
                    {/* Top Bar - Auto-hide */}
                    <div className="absolute top-0 left-0 right-0 z-50 transition-all duration-300 translate-y-0 opacity-100">
                        <div className="bg-gray-800 text-white px-4 py-2 flex items-center justify-between shadow-lg border-b border-gray-700">
                        <div className="flex items-center gap-3">
                            <div className="w-7 h-7 bg-purple-600 rounded-lg flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M23 7l-7 5 7 5V7z"></path>
                                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-xs font-semibold">{meetingTitle}</h3>
                                <p className="text-[10px] text-gray-400">{participantName}</p>
                            </div>
                            {isConnected && (
                                <span className="flex items-center gap-1.5 text-[10px] text-green-400">
                                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                                    Live
                                    {userRole === 'teacher' && meetingStartTime && (
                                        <span className="text-gray-300 ml-2">
                                            ({(() => {
                                                const duration = (Date.now() - meetingStartTime) / (1000 * 60); // minutes
                                                const hours = Math.floor(duration / 60);
                                                const minutes = Math.floor(duration % 60);
                                                return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                                            })()})
                                        </span>
                                    )}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Recording Button */}
                            {userRole === 'teacher' && (
                                <button
                                    onClick={async () => {
                                        // Prevent multiple clicks
                                        if (isRecordingLoading) {
                                            console.log('⏸️ Recording operation already in progress...');
                                            return;
                                        }
                                        
                                        try {
                                            setIsRecordingLoading(true);
                                            if (isRecording) {
                                                console.log('🛑 Stopping recording via top button...');
                                                await stopRecordingIfActive();
                                                console.log('✅ Recording stopped via top button');
                                            } else {
                                                console.log(`▶️ Starting ${recordingPreference} recording via top button...`);
                                                if (recordingPreference === 'cloud') {
                                                    await startCloudRecording();
                                                } else {
                                                    await startLocalRecording();
                                                }
                                                console.log('✅ Recording started via top button');
                                            }
                                        } catch (error) {
                                            console.error('❌ Error in recording button:', error);
                                            toast.error('Failed to toggle recording');
                                        } finally {
                                            setIsRecordingLoading(false);
                                        }
                                    }}
                                    disabled={isLoading || isRecordingLoading}
                                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                        isRecording 
                                            ? 'bg-red-600 hover:bg-red-700' 
                                            : 'bg-gray-700 hover:bg-gray-600'
                                    } ${(isLoading || isRecordingLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    title={isRecording ? `Stop Recording (${recordingPreference.toUpperCase()})` : `Start Recording (${recordingPreference.toUpperCase()})`}
                                >
                                    {isRecordingLoading ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : isRecording ? (
                                        <div className="w-3 h-3 bg-white rounded-sm"></div>
                                    ) : (
                                        <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>
                                    )}
                                </button>
                            )}
                            
                            {/* Meeting Info Button */}
                            <button
                                onClick={() => openExclusivePanel('meetingInfo')}
                                className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                                    showMeetingInfo 
                                        ? 'bg-purple-600 hover:bg-purple-700' 
                                        : 'bg-gray-700 hover:bg-gray-600'
                                }`}
                                title="Meeting Info"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                            </button>

                            {/* Meeting Info Dialog */}
                            {showMeetingInfo && (
                                <div className="absolute top-12 right-4 z-50 bg-gray-800 rounded-lg shadow-2xl border border-gray-700 w-[400px]">
                                    <div className="p-4">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="text-white font-semibold">Meeting Information</h3>
                                            <button
                                                onClick={() => setShowMeetingInfo(false)}
                                                className="text-gray-400 hover:text-white transition-colors"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M18 6L6 18M6 6l12 12"></path>
                                                </svg>
                                            </button>
                                        </div>

                                        <div className="space-y-4">
                                            {/* Meeting Title */}
                                            <div>
                                                <label className="block text-gray-400 text-xs mb-1">Meeting Name</label>
                                                <p className="text-white text-sm font-medium">{meetingTitle}</p>
                                            </div>

                                            {/* Meeting ID */}
                                            <div>
                                                <label className="block text-gray-400 text-xs mb-1">Meeting ID</label>
                                                <div className="flex items-center gap-2">
                                                    <p className="text-white text-sm font-medium">{roomName}</p>
                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(roomName);
                                                            toast.success('Meeting ID copied to clipboard');
                                                        }}
                                                        className="text-blue-400 hover:text-blue-300"
                                                        title="Copy Meeting ID"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Participant Info */}
                                            <div>
                                                <label className="block text-gray-400 text-xs mb-1">Your Info</label>
                                                <div className="flex items-center gap-2">
                                                    <p className="text-white text-sm font-medium">{participantName}</p>
                                                    <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full">
                                                        {userRole === 'teacher' ? 'Teacher' : 'Student'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Join Time */}
                                            <div>
                                                <label className="block text-gray-400 text-xs mb-1">Joined At</label>
                                                <p className="text-white text-sm font-medium">
                                                    {new Date().toLocaleTimeString()}
                                                </p>
                                            </div>

                                            {/* Subscription Hours Remaining */}
                                            {userRole === 'teacher' && subscriptionHoursRemaining !== null && (
                                                <div>
                                                    <label className="block text-gray-400 text-xs mb-1">Hours Remaining</label>
                                                    <p className={`text-sm font-medium ${
                                                        subscriptionHoursRemaining <= 1 ? 'text-red-400' : 
                                                        subscriptionHoursRemaining <= 5 ? 'text-yellow-400' : 'text-green-400'
                                                    }`}>
                                                        {subscriptionHoursRemaining.toFixed(1)} hours
                                                    </p>
                                                </div>
                                            )}

                                            {/* Meeting Duration (for teacher) */}
                                            {userRole === 'teacher' && meetingStartTime && (
                                                <div>
                                                    <label className="block text-gray-400 text-xs mb-1">Duration</label>
                                                    <p className="text-white text-sm font-medium">
                                                        {(() => {
                                                            const duration = (Date.now() - meetingStartTime) / (1000 * 60);
                                                            const hours = Math.floor(duration / 60);
                                                            const minutes = Math.floor(duration % 60);
                                                            return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                                                        })()}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Invite Link */}
                                            <div>
                                                <label className="block text-gray-400 text-xs mb-1">Invite Link</label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="text"
                                                        value={`${window.location.origin}/meet/join?room=${roomName}&title=${encodeURIComponent(meetingTitle || 'Meeting')}&subscription=${encodeURIComponent(subscriptionName || '')}&tenant=${tenantId}`}
                                                        readOnly
                                                        className="flex-1 bg-gray-700 text-white px-3 py-1.5 rounded text-sm border border-gray-600"
                                                    />
                                                    <button
                                                        onClick={() => {
                                                            const inviteLink = `${window.location.origin}/meet/join?room=${roomName}&title=${encodeURIComponent(meetingTitle || 'Meeting')}&subscription=${encodeURIComponent(subscriptionName || '')}&tenant=${tenantId}`;
                                                            navigator.clipboard.writeText(inviteLink);
                                                            toast.success('Meeting link copied to clipboard');
                                                        }}
                                                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors"
                                                    >
                                                        Copy
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                                {/* Chat Button */}
                                <button
                                    onClick={() => {
                                        console.log('💬 Chat button clicked');
                                        openExclusivePanel('chat');
                                    }}
                                    className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors relative ${
                                        showChat
                                            ? 'bg-purple-600 hover:bg-purple-700' 
                                            : 'bg-gray-700 hover:bg-gray-600'
                                    }`}
                                    title="Chat"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                    </svg>
                                    {unreadMessageCount > 0 && (
                                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                                            {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                                        </span>
                                    )}
                                </button>
                            

                                {/* Video Settings Button */}
                                <button
                                    onClick={() => {
                                        console.log('⚙️ Settings button clicked');
                                        openExclusivePanel('background');
                                    }}
                                    className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                                        showBackgroundEffects
                                            ? 'bg-purple-600 hover:bg-purple-700' 
                                            : 'bg-gray-700 hover:bg-gray-600'
                                    }`}
                                    title="Video Settings"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="3"></circle>
                                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                                    </svg>
                                </button>
                            
                            {/* Chess Board Toggle - Teacher only */}
                            {userRole === 'teacher' && (
                                <button
                                    onClick={() => openExclusivePanel('chess')}
                                    className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                                        showChessBoard 
                                            ? 'bg-purple-600 hover:bg-purple-700' 
                                            : 'bg-gray-700 hover:bg-gray-600'
                                    }`}
                                    title={showChessBoard ? 'Close Puzzle Mode' : 'Open Puzzle Library'}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 2l2 4h4l-3 3 1 4-4-2-4 2 1-4-3-3h4z"></path>
                                        <rect x="2" y="14" width="20" height="8" rx="1"></rect>
                                    </svg>
                                </button>
                            )}
                            
                            {/* Polls/Quizzes Toggle - Teacher only */}
                            {userRole === 'teacher' && (
                                <button
                                    onClick={() => openExclusivePanel('polls')}
                                    className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                                        showPollsQuizzes 
                                            ? 'bg-blue-600 hover:bg-blue-700' 
                                            : 'bg-gray-700 hover:bg-gray-600'
                                    }`}
                                    title={showPollsQuizzes ? 'Close Polls' : 'Open Polls & Quizzes'}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                                        <path d="M9 9h6v6H9z"></path>
                                    </svg>
                                </button>
                            )}
                            
                            {/* Leave Meeting Button */}
                            <button
                                onClick={handleTeacherLeave}
                                className="w-7 h-7 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center transition-colors"
                                title={userRole === 'teacher' ? 'Leave Meeting (End for all?)' : 'Leave Meeting'}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                    <polyline points="16,17 21,12 16,7"></polyline>
                                    <line x1="21" y1="12" x2="9" y2="12"></line>
                                </svg>
                            </button>
                        </div>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="flex-1 relative overflow-hidden">
                        {/* Loading overlay - only show during initial connection, not during active meetings */}
                        {isLoading && !isConnected && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-10 w-10 border-3 border-purple-500 border-t-transparent mx-auto mb-3"></div>
                                    <p className="text-white text-xs font-medium">Connecting...</p>
                                    <p className="text-gray-500 text-[10px] mt-1">This may take a few seconds</p>
                                </div>
                            </div>
                        )}
                        
                        {/* Hours Remaining Banner - Top of Meeting */}
                        {showHoursInfoDialog && subscriptionHoursRemaining !== null && userRole === 'teacher' && (
                            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
                                <div className={`px-6 py-3 rounded-lg shadow-2xl border-2 flex items-center gap-3 ${
                                    subscriptionHoursRemaining <= 1 
                                        ? 'bg-red-900/95 border-red-600 text-red-100' 
                                        : subscriptionHoursRemaining <= 5 
                                        ? 'bg-yellow-900/95 border-yellow-600 text-yellow-100'
                                        : 'bg-green-900/95 border-green-600 text-green-100'
                                } backdrop-blur-sm`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="12 6 12 12 16 14"></polyline>
                                    </svg>
                                    <div>
                                        <div className="text-sm font-bold">
                                            {subscriptionHoursRemaining.toFixed(1)} hours remaining
                                        </div>
                                        {subscriptionHoursRemaining <= 5 && (
                                            <div className="text-xs opacity-90 mt-0.5">
                                                {subscriptionHoursRemaining <= 1 ? 'Consider purchasing more hours' : 'Running low - purchase more soon'}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => setShowHoursInfoDialog(false)}
                                        className="ml-2 text-current opacity-70 hover:opacity-100 transition-opacity"
                                        title="Dismiss"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        <div 
                            className={
                                showPollsQuizzes ? 'lk-video-with-polls' :
                            showChat ? (chatExpanded ? 'lk-video-with-chat-expanded' : 'lk-video-with-chat') : 
                            showChessBoard ? 'lk-video-with-chess' : 
                            'lk-video-full'
                            }
                            style={showPollsQuizzes ? { width: `calc(100% - ${pollsPanelWidth}px)` } : {}}
                        >
                            <div className="w-full h-full relative">
                            <VideoConference />
                                {/* Prevent LiveKit default loading overlays from covering video during active meetings */}
                                {/* Hide default video tiles when custom layout is active - but keep VideoConference visible for tracks */}
                                {isConnected && (
                                    <style>{`
                                        .lk-video-conference .lk-loading,
                                        .lk-video-conference .lk-connecting,
                                        .lk-video-conference::before,
                                        .lk-video-conference::after,
                                        .lk-room-container .lk-loading,
                                        .lk-room-container .lk-connecting,
                                        [class*="lk-loading"],
                                        [class*="lk-connecting"] {
                                            display: none !important;
                                            opacity: 0 !important;
                                            visibility: hidden !important;
                                        }
                                        /* Hide default LiveKit participant tiles when custom layout is active */
                                        /* But ensure VideoConference container itself remains visible for video tracks */
                                        .lk-video-conference {
                                            position: absolute !important;
                                            top: 0 !important;
                                            left: 0 !important;
                                            width: 100% !important;
                                            height: 100% !important;
                                            z-index: 0 !important;
                                            pointer-events: none !important;
                                        }
                                        .lk-video-conference .lk-participant-tile,
                                        .lk-video-conference .lk-participant-name,
                                        .lk-video-conference .lk-grid-layout,
                                        .lk-video-conference .lk-focus-layout,
                                        .lk-video-conference .lk-grid-layout-wrapper,
                                        .lk-video-conference .lk-focus-layout-wrapper,
                                        .lk-video-conference .lk-participant-tile * {
                                            display: none !important;
                                            visibility: hidden !important;
                                            opacity: 0 !important;
                                            pointer-events: none !important;
                                        }
                                        /* Ensure video tracks are still accessible but hidden */
                                        .lk-video-conference video {
                                            display: none !important;
                                        }
                                    `}</style>
                                )}
                            </div>
                            <RoomAudioRenderer />
                            
                            {/* Custom Layout Manager - Always present but conditionally shows layouts */}
                            <CustomLayoutManager
                                currentLayout={currentLayout}
                                userRole={userRole}
                                teacherParticipantId={teacherParticipantId}
                                pinnedParticipant={pinnedParticipant}
                                onParticipantPin={handleParticipantPin}
                                onTeacherIdentified={handleTeacherIdentified}
                                onScreenShareChange={handleScreenShareDetected}
                                isMirrored={isMirrored}
                            />
                            {minimizeVideos && (
                                <style>{`
                                    .lk-participant-tile:not([data-lk-source="screen_share"]) {
                                        display: none !important;
                                    }
                                    .lk-participant-tile[data-lk-source="screen_share"] {
                                        width: 100% !important;
                                        height: 100% !important;
                                    }
                                `}</style>
                            )}
                            
                            {/* Recording Indicator */}
                            {isRecording && (
                                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50">
                                    <div className="flex items-center gap-2 bg-red-600 text-white rounded-full px-3 py-1 shadow-lg border border-red-400">
                                        <span className="inline-block w-2.5 h-2.5 bg-white rounded-full animate-pulse"></span>
                                        <span className="text-xs font-semibold tracking-wide">REC</span>
                                        <span className="text-[10px] opacity-90">
                                            {(() => {
                                                const started = recordingStartMs ?? Date.now();
                                                const secs = Math.max(0, Math.floor((Date.now() - started) / 1000));
                                                const mm = String(Math.floor(secs / 60)).padStart(2, '0');
                                                const ss = String(secs % 60).padStart(2, '0');
                                                return `${mm}:${ss}`;
                                            })()}
                                        </span>
                                        <span className="text-[10px] opacity-90">{recordingMode?.toUpperCase()}</span>
                                    </div>
                                </div>
                            )}
                            
                            {/* Activity notification listener for students */}
                            <ActivityNotificationListener 
                                userRole={userRole} 
                                onPuzzleShared={handlePuzzleShared}
                                onPollStarted={handlePollStarted}
                                onChatMessage={handleChatMessage}
                                onShowChessBoard={setShowChessBoard}
                                onShowPollsQuizzes={setShowPollsQuizzes}
                            />
                        </div>

                        {/* Participants Side Panel */}
                        {showParticipantsPanel && (
                            <ParticipantsPanel
                                roomRef={lkRoomRef}
                                userRole={userRole}
                                onClose={() => setShowParticipantsPanel(false)}
                            />
                        )}


                        {/* Custom Control Panel - Auto-hide */}
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[2001] transition-all duration-300 translate-y-0 opacity-100">
                            <div className="bg-gray-800 rounded-full px-4 py-2 flex items-center gap-3 shadow-lg border border-gray-700">
                                {/* Subscription Hours Balance - Prominent display for teachers */}
                                {userRole === 'teacher' && subscriptionHoursRemaining !== null && (
                                    <div className={`px-3 py-1.5 rounded-full flex items-center gap-2 border ${
                                        subscriptionHoursRemaining <= 1 
                                            ? 'bg-red-900/50 border-red-600 text-red-200' 
                                            : subscriptionHoursRemaining <= 5 
                                            ? 'bg-yellow-900/50 border-yellow-600 text-yellow-200'
                                            : 'bg-green-900/50 border-green-600 text-green-200'
                                    }`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <polyline points="12 6 12 12 16 14"></polyline>
                                        </svg>
                                        <span className="text-xs font-semibold whitespace-nowrap">
                                            {subscriptionHoursRemaining.toFixed(1)}h left
                                        </span>
                                    </div>
                                )}
                                
                                {/* Participants Panel Toggle */}
                                <button
                                    onClick={() => setShowParticipantsPanel(!showParticipantsPanel)}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${showParticipantsPanel ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-700 hover:bg-gray-600'}`}
                                    title="Show Participants"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="12" cy="7" r="4"></circle>
                                    </svg>
                                </button>
                                {/* Mute Button */}
                                <button
                                    onClick={async () => {
                                        console.log('🎤 Mute button clicked');
                                        console.log('Room ref:', lkRoomRef.current);
                                        console.log('Local participant:', lkRoomRef.current?.localParticipant);
                                        
                                        if (!lkRoomRef.current?.localParticipant) {
                                            console.log('❌ No room or participant available');
                                            toast.error('Not connected to meeting');
                                            return;
                                        }
                                        
                                        const participant = lkRoomRef.current.localParticipant;
                                        const currentState = participant.isMicrophoneEnabled;
                                        const newState = !currentState;
                                        
                                        try {
                                            console.log(`🎤 Current mic state: ${currentState}, toggling to: ${newState}`);
                                            
                                            // Update UI state immediately for better responsiveness
                                            setMicEnabled(newState);
                                            
                                            // Toggle microphone state
                                            await participant.setMicrophoneEnabled(newState);
                                            
                                            // Force re-render to ensure UI updates
                                            setForceUpdate(prev => prev + 1);
                                            
                                            console.log(`🎤 Microphone ${newState ? 'enabled' : 'disabled'}`);
                                            toast.success(`Microphone ${newState ? 'unmuted' : 'muted'}`);
                                        } catch (error) {
                                            console.error('Error toggling microphone:', error);
                                            toast.error('Failed to toggle microphone');
                                            // Revert UI state on error
                                            setMicEnabled(currentState);
                                        }
                                    }}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                        micEnabled
                                            ? 'bg-gray-700 hover:bg-gray-600'
                                            : 'bg-red-600 hover:bg-red-700'
                                    }`}
                                    title="Mute/Unmute"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {micEnabled ? (
                                            <>
                                                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                                                <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                                                <line x1="12" y1="19" x2="12" y2="23"></line>
                                                <line x1="8" y1="23" x2="16" y2="23"></line>
                                            </>
                                        ) : (
                                            <>
                                                <line x1="1" y1="1" x2="23" y2="23"></line>
                                                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                                                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2"></path>
                                                <line x1="12" y1="19" x2="12" y2="23"></line>
                                                <line x1="8" y1="23" x2="16" y2="23"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>

                                {/* Audio Device Selection */}
                                {audioDevices.length > 1 && (
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowAudioDevices(!showAudioDevices)}
                                            className="w-6 h-6 rounded-full bg-gray-600 hover:bg-gray-500 flex items-center justify-center transition-colors"
                                            title="Select Microphone"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="6,9 12,15 18,9"></polyline>
                                            </svg>
                                        </button>
                                        
                                        {showAudioDevices && (
                                            <div className="absolute bottom-full left-0 mb-2 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 max-h-32 overflow-y-auto">
                                                {audioDevices.map((device) => (
                                                    <button
                                                        key={device.deviceId}
                                                        onClick={() => switchAudioDevice(device.deviceId)}
                                                        className={`w-full px-3 py-2 text-left text-xs hover:bg-gray-700 transition-colors ${
                                                            device.deviceId === selectedAudioDevice ? 'bg-purple-600 text-white' : 'text-gray-300'
                                                        }`}
                                                    >
                                                        {device.label || `Mic ${device.deviceId.slice(0, 8)}`}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Camera Button */}
                                <button
                                    onClick={async () => {
                                        console.log('📹 Camera button clicked');
                                        console.log('Room ref:', lkRoomRef.current);
                                        console.log('Local participant:', lkRoomRef.current?.localParticipant);
                                        
                                        if (!lkRoomRef.current?.localParticipant) {
                                            console.log('❌ No room or participant available');
                                            toast.error('Not connected to meeting');
                                            return;
                                        }
                                        
                                        const participant = lkRoomRef.current.localParticipant;
                                        const currentState = participant.isCameraEnabled;
                                        const newState = !currentState;
                                        
                                        try {
                                            console.log(`📹 Current camera state: ${currentState}, toggling to: ${newState}`);
                                            
                                            // Update UI state immediately for better responsiveness
                                            setCameraEnabled(newState);
                                            
                                            // Toggle camera state
                                            await participant.setCameraEnabled(newState);
                                            
                                            // Force re-render to ensure UI updates
                                            setForceUpdate(prev => prev + 1);
                                            
                                            console.log(`📹 Camera ${newState ? 'enabled' : 'disabled'}`);
                                            toast.success(`Camera ${newState ? 'turned on' : 'turned off'}`);
                                        } catch (error) {
                                            console.error('Error toggling camera:', error);
                                            toast.error('Failed to toggle camera');
                                            // Revert UI state on error
                                            setCameraEnabled(currentState);
                                        }
                                    }}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                        cameraEnabled
                                            ? 'bg-gray-700 hover:bg-gray-600'
                                            : 'bg-red-600 hover:bg-red-700'
                                    }`}
                                    title="Turn Camera On/Off"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {cameraEnabled ? (
                                            <>
                                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                                                <circle cx="12" cy="13" r="4"></circle>
                                            </>
                                        ) : (
                                            <>
                                                <line x1="1" y1="1" x2="23" y2="23"></line>
                                                <path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h.93L16 18.11V19"></path>
                                                <path d="M7 7h.01"></path>
                                            </>
                                        )}
                                    </svg>
                                </button>

                                {/* Video Device Selection */}
                                {videoDevices.length > 1 && (
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowVideoDevices(!showVideoDevices)}
                                            className="w-6 h-6 rounded-full bg-gray-600 hover:bg-gray-500 flex items-center justify-center transition-colors"
                                            title="Select Camera"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="6,9 12,15 18,9"></polyline>
                                            </svg>
                                        </button>
                                        
                                        {showVideoDevices && (
                                            <div className="absolute bottom-full left-0 mb-2 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 max-h-32 overflow-y-auto">
                                                {videoDevices.map((device) => (
                                                    <button
                                                        key={device.deviceId}
                                                        onClick={() => switchVideoDevice(device.deviceId)}
                                                        className={`w-full px-3 py-2 text-left text-xs hover:bg-gray-700 transition-colors ${
                                                            device.deviceId === selectedVideoDevice ? 'bg-purple-600 text-white' : 'text-gray-300'
                                                        }`}
                                                    >
                                                        {device.label || `Cam ${device.deviceId.slice(0, 8)}`}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Share Screen Button - available to teacher even when chequered is open */}
                                {userRole === 'teacher' && (
                                    <button
                                        onClick={() => {
                                            if (lkRoomRef.current?.localParticipant) {
                                                if (isScreenSharing) {
                                                    // Stop screen sharing
                                                    lkRoomRef.current.localParticipant.setScreenShareEnabled(false);
                                                } else {
                                                    // Start screen sharing with default picker to allow sharing this tab/window
                                                    lkRoomRef.current.localParticipant.setScreenShareEnabled(true);
                                                }
                                            }
                                        }}
                                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                            isScreenSharing 
                                                ? 'bg-red-600 hover:bg-red-700' 
                                                : 'bg-gray-700 hover:bg-gray-600'
                                        }`}
                                        title={isScreenSharing ? "Stop Sharing" : "Share Screen"}
                                    >
                                        {isScreenSharing ? (
                                            // Stop sharing icon
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                                                <line x1="8" y1="21" x2="16" y2="21"></line>
                                                <line x1="12" y1="17" x2="12" y2="21"></line>
                                                <line x1="15" y1="9" x2="9" y2="15"></line>
                                                <line x1="9" y1="9" x2="15" y2="15"></line>
                                            </svg>
                                        ) : (
                                            // Start sharing icon
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                                                <line x1="8" y1="21" x2="16" y2="21"></line>
                                                <line x1="12" y1="17" x2="12" y2="21"></line>
                                            </svg>
                                        )}
                                    </button>
                                )}

                                {/* Minimize Videos (Student focus on shared content) */}
                                {userRole === 'student' && currentLayout === 'screen-share' && !isScreenSharing && (
                                    <button
                                        onClick={() => setMinimizeVideos(!minimizeVideos)}
                                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                            minimizeVideos ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-700 hover:bg-gray-600'
                                        }`}
                                        title={minimizeVideos ? 'Show Videos' : 'Hide Videos'}
                                    >
                                        {minimizeVideos ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                                                <polyline points="6,9 12,15 18,9"></polyline>
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                                                <polyline points="6,15 12,9 18,15"></polyline>
                                            </svg>
                                        )}
                                    </button>
                                )}

                                {/* Leave Meeting Button */}
                                <button
                                    onClick={handleTeacherLeave}
                                    className="w-10 h-10 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center transition-colors"
                                    title="Leave Meeting"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                        <polyline points="16,17 21,12 16,7"></polyline>
                                        <line x1="21" y1="12" x2="9" y2="12"></line>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Chat Panel - Zoom Style */}
                        {showChat && (
                            <div className={`lk-custom-chat-panel ${chatExpanded ? 'expanded' : ''}`}>
                                {/* Chat Header */}
                                <div>
                                    <h4>Chat</h4>
                                    <div className="flex items-center gap-2">
                                        {/* Expand/Collapse Button */}
                                        <button 
                                            onClick={toggleChatExpansion}
                                            className="text-gray-400 hover:text-white transition-colors"
                                            title={chatExpanded ? 'Collapse Chat' : 'Expand Chat'}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                {chatExpanded ? (
                                                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
                                                ) : (
                                                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                                                )}
                                            </svg>
                                        </button>
                                        {/* Close Button */}
                                        <button 
                                            onClick={() => {
                                                setShowChat(false);
                                                setUnreadMessageCount(0);
                                            }} 
                                            className="text-gray-400 hover:text-white transition-colors"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M18 6L6 18M6 6l12 12"></path>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Chat Messages */}
                                <div>
                                    {console.log('🔍 Current chat messages:', chatMessages)}
                                    {chatMessages.length === 0 ? (
                                        <div className="text-center mt-8">
                                            <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                                </svg>
                                            </div>
                                            <p className="text-gray-400 text-sm">No messages yet</p>
                                            <p className="text-gray-500 text-xs mt-1">Start a conversation!</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {chatMessages.map((msg, idx) => (
                                                <div key={idx} className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-blue-400 font-semibold text-sm">{msg.name}</span>
                                                        <span className="text-gray-500 text-xs">{msg.time}</span>
                                                    </div>
                                                    <p className="text-gray-200 text-sm leading-relaxed">{msg.message}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Chat Input */}
                                <div>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={chatInput}
                                            onChange={(e) => setChatInput(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                                            placeholder="Type a message..."
                                            className="flex-1 bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm placeholder-gray-400"
                                        />
                                        <button
                                            onClick={sendChatMessage}
                                            disabled={!chatInput.trim()}
                                            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm font-medium"
                                        >
                                            Send
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}


                        {/* Background Effects Panel */}
                        {showBackgroundEffects && (
                            <BackgroundEffectsControl
                                onEffectChange={(effect, imageUrl) => {
                                    console.log('🎨 Effect changed:', effect, imageUrl);
                                    setCurrentEffect(effect);
                                    // Handle effect change logic
                                }}
                                currentEffect={currentEffect}
                                showPanel={showBackgroundEffects}
                                onClose={() => setShowBackgroundEffects(false)}
                                onVideoEnhancementChange={() => {
                                    // Handle video enhancement change
                                }}
                                isMirrored={isMirrored}
                                onMirrorToggle={toggleMirror}
                                isRecording={isRecording}
                                selectedCloudRes={selectedCloudRes}
                                setSelectedCloudRes={setSelectedCloudRes}
                                selectedLocalRes={selectedLocalRes}
                                setSelectedLocalRes={setSelectedLocalRes}
                                recordLayout={recordLayout}
                                setRecordLayout={setRecordLayout}
                                stopRecordingIfActive={stopRecordingIfActive}
                                startCloudRecording={startCloudRecording}
                                startLocalRecording={startLocalRecording}
                                recordingPreference={recordingPreference}
                                setRecordingPreference={setRecordingPreference}
                                selectedAvatar={selectedAvatar}
                                onAvatarChange={(avatarUrl) => {
                                    // Handle avatar change
                                    console.log('Avatar changed:', avatarUrl);
                                }}
                            />
                        )}

                        {/* Chess Board Panel - Zoom Style */}
                        {showChessBoard && (
                            <div className="lk-custom-chess-panel">
                                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
                                    <div className="flex items-center gap-2">
                                        <h4 className="text-white text-base font-semibold">♟️ Chess Puzzle Mode</h4>
                                        {userRole === 'teacher' && (
                                            <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">
                                                Teacher Mode
                                            </span>
                                        )}
                                        {userRole === 'student' && (
                                            <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                                                Student Mode
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => setShowChessBoard(false)} 
                                        className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-700"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M18 6L6 18M6 6l12 12"></path>
                                        </svg>
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-4">
                                    <ChessInMeeting
                                        subscriptionId={subscriptionId}
                                        subscriptionName={subscriptionName}
                                        userRole={userRole}
                                        participantName={participantName}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Polls/Quizzes Panel - Zoom Style */}
                        {showPollsQuizzes && (
                            <div 
                                className="lk-custom-polls-panel" 
                                style={{ width: `${pollsPanelWidth}px` }}
                            >
                                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
                                    <div className="flex items-center gap-2">
                                        <h4 className="text-white text-base font-semibold">📊 Polls & Quizzes</h4>
                                        {userRole === 'teacher' && (
                                            <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">
                                                Teacher Mode
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {/* Resize Controls */}
                                        <button
                                            onClick={() => setPollsPanelWidth(300)}
                                            className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-700"
                                            title="Narrower"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M5 12h14M12 19l-7-7 7-7"></path>
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => setPollsPanelWidth(500)}
                                            className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-700"
                                            title="Wider"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M12 5l7 7-7 7M19 12H5"></path>
                                            </svg>
                                        </button>
                                        
                                        {/* Close Button */}
                                    <button
                                        onClick={() => setShowPollsQuizzes(false)} 
                                        className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-700"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M18 6L6 18M6 6l12 12"></path>
                                        </svg>
                                    </button>
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto p-4">
                                    <PollsInMeeting
                                        subscriptionId={subscriptionId}
                                        subscriptionName={subscriptionName}
                                        userRole={userRole}
                                        participantName={participantName}
                                        tenantId={tenantId}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </LiveKitRoom>
        </div>
        </>
    );

    return (
        <>
            {createPortal(meetingContent, document.body)}
            
            {/* Purchase Hours Dialog */}
            {showPurchaseHoursDialog && (
                <div className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4">
                    <div className="bg-gray-900 rounded-lg border border-gray-700 shadow-2xl max-w-md w-full">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-xl font-bold text-white">⏰ Meeting Hours Required</h3>
                                <button
                                    onClick={() => setShowPurchaseHoursDialog(false)}
                                    className="text-gray-400 hover:text-white transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                            
                            <div className="mb-6">
                                <p className="text-gray-300 mb-4">
                                    You need more meeting hours to continue. Your current balance is insufficient to start or continue meetings.
                                </p>
                                
                                <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 mb-4">
                                    <div className="flex items-center gap-2 text-red-300">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="12" y1="8" x2="12" y2="12"></line>
                                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                        </svg>
                                        <span className="font-semibold">Low Balance</span>
                                    </div>
                                    {subscriptionHoursRemaining !== null && subscriptionHoursRemaining > 0 && (
                                        <p className="text-sm text-gray-400 mt-2">
                                            Current balance: {subscriptionHoursRemaining.toFixed(2)} hours (minimum 1 hour required)
                                        </p>
                                    )}
                                    {subscriptionHoursRemaining !== null && subscriptionHoursRemaining <= 0 && (
                                        <p className="text-sm text-gray-400 mt-2">
                                            Current balance: 0 hours (minimum 1 hour required)
                                        </p>
                                    )}
                                </div>
                            </div>
                            
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowPurchaseHoursDialog(false)}
                                    className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        setShowPurchaseHoursDialog(false);
                                        // Navigate to purchase page or open purchase modal
                                        // TODO: Implement navigation to purchase hours page
                                        window.location.href = '/subscriptions/purchase-hours'; // Update this path as needed
                                        toast.info('Redirecting to purchase page...');
                                    }}
                                    className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium"
                                >
                                    Purchase Hours
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// Participants Panel Component - Zoom-style UI (Dark mode + search)
const ParticipantsPanel: React.FC<{
    roomRef: React.MutableRefObject<any>;
    userRole: string;
    onClose: () => void;
}> = ({ roomRef, userRole, onClose }) => {
    const participants = useParticipants();
    const { localParticipant } = useLocalParticipant();
    const [mutingParticipant, setMutingParticipant] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const isTeacher = userRole === 'teacher';

    // Force mute/unmute participant using LiveKit Cloud permissions
    const handleMuteToggle = async (p: any) => {
        if (!roomRef.current) return;
        
        const participantId = p.identity;
        const isCurrentlyMuted = !p.isMicrophoneEnabled;
        const targetMuted = !isCurrentlyMuted; // Toggle: mute if not muted, unmute if muted

        setMutingParticipant(participantId);
        
        try {
            // Use LiveKit's direct API - requires proper permissions in token
            if (typeof p.setMicrophoneEnabled === 'function') {
                await p.setMicrophoneEnabled(!targetMuted);
                toast.success(`${targetMuted ? 'Muted' : 'Unmuted'} ${p.name || participantId}`);
            } else {
                // Fallback: Try through room if direct participant method unavailable
                const room = roomRef.current;
                const remoteParticipant = room?.participants?.get?.(participantId);
                if (remoteParticipant && typeof remoteParticipant.setMicrophoneEnabled === 'function') {
                    await remoteParticipant.setMicrophoneEnabled(!targetMuted);
                    toast.success(`${targetMuted ? 'Muted' : 'Unmuted'} ${p.name || participantId}`);
                } else {
                    toast.error('Unable to mute participant. Please check LiveKit permissions.');
                }
            }
        } catch (error: any) {
            console.error('Error toggling mute:', error);
            if (error.message?.includes('permission') || error.message?.includes('unauthorized')) {
                toast.error('Permission denied. Ensure teacher token has canUpdateOwnMetadata and canPublish permissions.');
            } else {
                toast.error(`Failed to ${targetMuted ? 'mute' : 'unmute'} participant`);
            }
        } finally {
            setMutingParticipant(null);
        }
    };

    // Sort participants: teacher first, then local participant, then others
    const allParticipants: any[] = localParticipant
        ? [localParticipant, ...participants.filter(p => p.identity !== localParticipant.identity)]
        : participants;

    // Sort: teacher first, then alphabetically
    const sortedParticipants = [...allParticipants].sort((a, b) => {
        const aIsTeacher = a.name?.toLowerCase().includes('teacher') || a.name === 'teacher';
        const bIsTeacher = b.name?.toLowerCase().includes('teacher') || b.name === 'teacher';
        if (aIsTeacher && !bIsTeacher) return -1;
        if (!aIsTeacher && bIsTeacher) return 1;
        return (a.name || a.identity || '').localeCompare(b.name || b.identity || '');
    });

    const filteredParticipants = sortedParticipants.filter((p: any) => {
        const target = (p.name || p.identity || '').toLowerCase();
        return target.includes(searchQuery.toLowerCase());
    });

    return (
        <div className="absolute right-3 bottom-20 top-3 w-80 bg-gray-900 rounded-lg border border-gray-700 z-50 shadow-2xl flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-700 bg-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="text-base font-semibold text-white">Participants</div>
                    <div className="text-sm text-gray-200 bg-gray-700 px-2 py-0.5 rounded-full">
                        {filteredParticipants.length}
                    </div>
                </div>
                <button 
                    onClick={onClose} 
                    className="text-gray-300 hover:text-white transition-colors p-1 hover:bg-gray-700 rounded"
                    title="Close"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>

            {/* Search */}
            <div className="px-3 py-2 border-b border-gray-700 bg-gray-900">
                <div className="relative">
                    <input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search participants"
                        className="w-full px-9 py-2 bg-gray-800 text-gray-200 placeholder-gray-400 rounded-md border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                </div>
            </div>

            {/* Participants List */}
            <div className="flex-1 overflow-y-auto bg-gray-900">
                {filteredParticipants.map((p: any) => {
                    const isLocal = localParticipant && p.identity === localParticipant.identity;
                    const name = p.name || p.identity || 'Participant';
                    const audioEnabled = typeof p.isMicrophoneEnabled === 'boolean' ? p.isMicrophoneEnabled : (p.audioTrackPublications?.size > 0);
                    const isMuting = mutingParticipant === p.identity;
                    const isName = name.length > 0;

                    return (
                        <div 
                            key={p.identity} 
                            className="px-4 py-2.5 hover:bg-gray-800 transition-colors border-b border-gray-800 last:border-b-0"
                        >
                            <div className="flex items-center gap-3">
                                {/* Avatar */}
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                                    isLocal ? 'bg-blue-600 text-white' : audioEnabled ? 'bg-green-700/30 text-green-300' : 'bg-gray-700 text-gray-300'
                                }`}>
                                    {isName ? name.slice(0, 1).toUpperCase() : '?'}
                                </div>

                                {/* Name and Status */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <div className="text-sm font-medium text-gray-100 truncate">
                                            {name}
                                        </div>
                                        {isLocal && (
                                            <span className="text-xs text-blue-400 font-medium">(You)</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <div className={`flex items-center gap-1 text-xs ${
                                            audioEnabled ? 'text-green-400' : 'text-red-400'
                                        }`}>
                                            {audioEnabled ? (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-400">
                                                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                                                        <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                                                        <line x1="12" y1="19" x2="12" y2="23"></line>
                                                        <line x1="8" y1="23" x2="16" y2="23"></line>
                                                    </svg>
                                                    <span>Unmuted</span>
                                                </>
                                            ) : (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-400">
                                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                                        <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                                                        <path d="M17 16.95A7 7 0 0 1 5 12v-2"></path>
                                                        <line x1="12" y1="19" x2="12" y2="23"></line>
                                                        <line x1="8" y1="23" x2="16" y2="23"></line>
                                                    </svg>
                                                    <span>Muted</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Mute/Unmute Button (Teacher only, not for self) */}
                                {isTeacher && !isLocal && (
                                    <button
                                        onClick={() => handleMuteToggle(p)}
                                        disabled={isMuting}
                                        className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                                            audioEnabled
                                                ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60 border border-red-800'
                                                : 'bg-green-900/40 text-green-300 hover:bg-green-900/60 border border-green-800'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                        title={audioEnabled ? 'Mute participant' : 'Unmute participant'}
                                    >
                                        {isMuting ? (
                                            <div className="flex items-center gap-1.5">
                                                <svg className="animate-spin text-gray-300" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="12" y1="2" x2="12" y2="6"></line>
                                                    <line x1="12" y1="18" x2="12" y2="22"></line>
                                                    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                                                    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                                                    <line x1="2" y1="12" x2="6" y2="12"></line>
                                                    <line x1="18" y1="12" x2="22" y2="12"></line>
                                                    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                                                    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
                                                </svg>
                                                <span className="text-gray-300">...</span>
                                            </div>
                                        ) : (
                                            audioEnabled ? 'Mute' : 'Unmute'
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-gray-700 bg-gray-800">
                <div className="text-xs text-gray-400 text-center">
                    {isTeacher ? 'Click Mute/Unmute to control participant audio' : 'View participants list'}
                </div>
            </div>
        </div>
    );
};

export default LiveKitRoomComponent;
